import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import { useConfirmLeave } from "../../settings/leave-guard.js";
import {
  Building2,
  Check,
  Copy,
  KeyRound,
  LogOut,
  MailPlus,
  MonitorSmartphone,
  RotateCcw,
  Search,
  ShieldCheck,
  StepForward,
  Trash2,
  UserPlus,
  Users,
  UserX,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTenantPeopleSource } from "@playbookai/api-clients";
import {
  Alert,
  AsyncSection,
  Avatar,
  AvatarFallback,
  Button,
  Card,
  CardBody,
  Checkbox,
  ConfirmDialog,
  DescriptionItem,
  DescriptionList,
  FormField,
  Input,
  Modal,
  Pagination,
  PeopleField,
  Select,
  SettingsGroup,
  StatusPill,
  TabPanel,
  Tabs,
  Tooltip,
  cn,
  toast,
  TableScroll,
  PIN_FIRST_COLUMN_CONTENT,
  type PeopleFieldLabels,
} from "@playbookai/ui";
import { AdminSection } from "./AdminSection.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";
import { useMailSendingState } from "./useMailSendingState.js";
import { useRowMutation } from "./useRowMutation.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { canAdministerWorkspace } from "../../auth/roles.js";
import {
  sameSession,
  scopeFromClaims,
  type SessionScope,
} from "../../auth/session-scope.js";
import {
  lookupMailDeliveries,
  type MailDeliveryNotice,
} from "../../api/mail-api.js";
import { SessionsModal } from "./SessionsModal.js";
import {
  TransformationLeadRemovalDialog,
  type TransformationLeadRemovalInput,
} from "./TransformationLeadRemovalDialog.js";
import { AccessPolicyPanel, useAccessPolicy } from "./AccessPolicyPanel.js";
import {
  InviteIcon,
  InvitesTabPanel,
  PendingInviteBadge,
  useInviteDialog,
} from "./InvitesPanel.js";
import {
  AdminApiError,
  TENANT_ADMIN_ROLE,
  TENANT_MEMBER_ROLE,
  TRANSFORMATION_LEAD_ROLE,
  adminDirectoryQueryKey,
  adminDirectoryQueryKeyPrefix,
  adminLamaOwnershipQueryKey,
  adminMembersNumberedQueryKey,
  adminMembersQueryKeyPrefix,
  baseAccessOf,
  composeTenantRoles,
  isTransformationLead,
  listAdminMembersPage,
  NUMBERED_MEMBER_PAGE_SIZE,
  type AdminMemberFilters,
  provisionDirectoryUser,
  provisionLocalUser,
  removeTransformationLead,
  resendSetPasswordEmail,
  resetMemberPassword,
  retryPasswordResetSignOut,
  searchDirectoryUsers,
  type AdminMember,
  type MemberPasswordResetResponse,
  type AdminMemberNumberedPage,
  type AuthenticationMethod,
  type BaseAccess,
  type DirectoryUser,
  type LocalUserProvisioningResponse,
  type MembershipStatus,
  type TenantRole,
  updateMembershipStatus,
  updateTenantRoles,
} from "../../api/admin-users-api.js";

/* Members & roles admin page (PB-42, lifted from the Workspace settings page
   PB-181 / pbdesign wireframe). Tenant-scoped, admin-only: the `/admin` route
   tree is gated once at its parent by RequireTenantAdmin (PB-48), so a
   non-admin deep-linking here gets the forbidden state and this page only
   mounts for an admin. The query `enabled` flags below stay as a second line
   of defence — the server remains the authoritative gate either way. */

/* BASE ACCESS only. Transformation Lead is a SEPARATE control beside every
   one of these selects (ADR-0078): the roles are additive and independent, and
   a single select listing all three would make them mutually exclusive — the
   plan's first listed risk. */
const ROLE_OPTIONS: { value: BaseAccess; label: string }[] = [
  { value: TENANT_MEMBER_ROLE, label: "Member" },
  { value: TENANT_ADMIN_ROLE, label: "Admin" },
];

/* The toolbar's role filter (PB-5178, d12). "Member" is not offered: every
   active member holds it, so it would filter nothing and read as a lie. */
const ROLE_FILTER_OPTIONS: { value: TenantRole | "all"; label: string }[] = [
  { value: "all", label: "All roles" },
  { value: TENANT_ADMIN_ROLE, label: "Admin" },
  { value: TRANSFORMATION_LEAD_ROLE, label: "Transformation Lead" },
];

const AUTH_METHOD_LABELS: Record<AuthenticationMethod, string> = {
  "microsoft-entra": "Microsoft Entra",
  local: "Local password",
};

/* Header and card-mode label classes of the roster table (Direction B). */
const TH =
  "px-4 py-3 text-start text-dense-1 font-medium text-pb-text-muted whitespace-nowrap";
const CARD_LABEL =
  "mb-1.5 block text-dense-1 font-medium text-pb-text-muted @min-[42.5rem]:hidden";
/* The extra Status pills (notified, sync, password, sign-out) wrap their text
   inside the fixed Status column rather than spilling into Last active: the
   "Send unconfirmed {date}" date follows the browser language and runs to
   ~245px in hu-HU or pt-BR (PB-5436). Layout only; their titles stay. */
const EXTRA_PILL = "max-w-full whitespace-normal";

const TRANSFORMATION_LEAD_HELP =
  "Admins manage this workspace. Transformation Leads manage LAMA questionnaires, campaigns and reports. The two are independent — a member can hold either, both or neither.";

/* PeopleField's words where its workspace wording would be false: this list
   searches Microsoft Entra, and from THREE letters, not two — the directory
   refuses fewer. PeopleField counts toward two, so the hint adds one, and a
   two-letter query (answered empty without a request) says what it needs
   rather than claiming nobody matches. */
const DIRECTORY_MIN_LETTERS = 3;
const directoryLettersNeeded = (count: number): string =>
  `Type ${count} more ${count === 1 ? "letter" : "letters"} to search the directory.`;
const DIRECTORY_FIELD_LABELS: PeopleFieldLabels = {
  searchPlaceholder: "Search by name or email",
  workspace: "Microsoft Entra directory",
  hint: (remaining, typed) =>
    typed
      ? directoryLettersNeeded(remaining + 1)
      : `Type ${DIRECTORY_MIN_LETTERS} letters to search the directory.`,
  searching: "Searching the directory",
  searchFailed: "Couldn’t search the directory right now.",
  // Counted on the trimmed query, as the field and the directory count it: a
  // trailing space is not a letter.
  noResults: (query) => {
    const short = DIRECTORY_MIN_LETTERS - query.trim().length;
    return short > 0
      ? directoryLettersNeeded(short)
      : `No one in the directory matches “${query}”.`;
  },
  noResultsHint: "Not in the directory? Use an email invitation below.",
};

function asBaseAccess(value: string): BaseAccess {
  return value === TENANT_ADMIN_ROLE ? TENANT_ADMIN_ROLE : TENANT_MEMBER_ROLE;
}

/* PB-5406. What an administrator is ASKED before a role change, and TOLD after
   any member write. Both halves were missing: granting Administrator or
   Transformation Lead applied on a single click, and no member action said
   anything at all afterwards — while suspend, remove, lead removal, invite
   revocation and module disablement all confirm. The rule the console actually
   follows is "a consequential member change confirms", so the access select
   confirms in BOTH directions; confirming a promotion but not a demotion would
   just move the inconsistency inside one control. */
type RoleConfirmation =
  | {
      readonly kind: "base-access";
      readonly member: AdminMember;
      readonly next: BaseAccess;
    }
  | { readonly kind: "lead-grant"; readonly member: AdminMember };

interface RoleConfirmationCopy {
  readonly title: string;
  readonly description: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly confirmVariant: "primary" | "destructive";
}

function roleConfirmationCopy(
  request: RoleConfirmation,
  who: string,
): RoleConfirmationCopy {
  if (request.kind === "lead-grant") {
    return {
      title: `Make ${who} a Transformation Lead?`,
      /* BOTH halves of the role, because this dialog exists to disclose what
         is being granted. ADR-0078's 2026-09-12 amendment (PB-5189) added KPI
         governance and says in as many words that "grants nothing outside
         LAMA" stopped being true — so naming only LAMA here would understate
         the authority in the one place an administrator is asked to weigh it. */
      description:
        "Transformation Leads create and run LAMA questionnaires and campaigns, read their reports, and govern KPIs — creating, updating, assigning owners, activating, archiving and restoring them.",
      /* Said here rather than at removal time, which is the only moment it
         would otherwise be discovered: dropping the role transfers everything
         the person owns to a named replacement first (ADR-0078), so this is
         not a grant that can be quietly undone. */
      body: "Removing the role later transfers whatever they own in LAMA to someone else first, so this is not a one-click change to reverse.",
      confirmLabel: "Make Transformation Lead",
      confirmVariant: "primary",
    };
  }
  return request.next === TENANT_ADMIN_ROLE
    ? {
        title: `Make ${who} an admin?`,
        description:
          "Admins invite and remove members, change roles, and change workspace settings.",
        body: "They keep everything they already have. Their Transformation Lead role, if they hold one, is unaffected.",
        confirmLabel: "Make admin",
        confirmVariant: "primary",
      }
    : {
        title: `Remove ${who}'s admin access?`,
        description:
          "They stay a member of this workspace and keep all of their work.",
        body: "They will no longer be able to manage members, roles or workspace settings. Their Transformation Lead role, if they hold one, is unaffected.",
        confirmLabel: "Remove admin access",
        confirmVariant: "destructive",
      };
}

/* What a completed member write says. One map rather than a sentence built at
   each call site, so the seven outcomes stay readable together and the
   wording cannot drift apart. */
type MemberChange =
  | "administrator-granted"
  | "administrator-revoked"
  | "lead-granted"
  | "lead-removed"
  | "suspended"
  | "reactivated"
  | "removed";

const MEMBER_CHANGE_MESSAGE: Record<MemberChange, (who: string) => string> = {
  "administrator-granted": (who) => `${who} is now an admin.`,
  "administrator-revoked": (who) => `${who} is no longer an admin.`,
  "lead-granted": (who) => `${who} is now a Transformation Lead.`,
  "lead-removed": (who) => `${who} is no longer a Transformation Lead.`,
  suspended: (who) => `${who} is suspended and cannot sign in.`,
  reactivated: (who) => `${who} has access again.`,
  removed: (who) => `${who} was removed from this workspace.`,
};

function memberChangeOfRoles(request: RoleConfirmation): MemberChange {
  if (request.kind === "lead-grant") return "lead-granted";
  return request.next === TENANT_ADMIN_ROLE
    ? "administrator-granted"
    : "administrator-revoked";
}

const MEMBER_STATUS_CHANGE: Record<
  "active" | "suspended" | "removed",
  MemberChange
> = {
  active: "reactivated",
  suspended: "suspended",
  removed: "removed",
};

type RosterMembershipStatus = Exclude<MembershipStatus, "removed">;

/* A query string is caller-supplied text, so it is matched against the known
   statuses rather than cast. Anything else falls back to "all", which is what an
   administrator opening a hand-edited link should see. */
export function initialStatusFilter(
  raw: string | null,
): RosterMembershipStatus | "all" {
  /* "removed" is deliberately NOT accepted: the roster lists current memberships and
     excludes soft-removed history, so a link asking for it would promise a view this
     page does not have. */
  return raw === "active" || raw === "suspended" ? raw : "all";
}

/* "all" is a client-only sentinel — the endpoint omits the `status` param to
   list current members (active and suspended). Removed memberships are retained
   as history and never belong in this operational roster. */
const STATUS_FILTER_OPTIONS: {
  value: RosterMembershipStatus | "all";
  label: string;
}[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];

/* PB-14 (IA-11). The server stays the authoritative gate: auth-access.service
   rejects self-demotion, self-deactivation and removing the last administrator
   with these codes. The UI mirrors only the guard it can evaluate soundly — the
   self row — and translates the rest. `last_admin` is deliberately NOT guarded
   client-side: memberRows holds just the pages fetched so far (50 a page), so
   counting admins here would block legitimate demotions on a large tenant. */
const ADMIN_ERROR_COPY: Record<string, string> = {
  self_lockout:
    "You cannot change your own role or membership. Ask another admin.",
  last_admin: "{member} is the last active admin. Promote someone else first.",
  membership_inactive: "{member} is not an active member of this workspace.",
  /* ADR-0078: a grant of Transformation Lead raced a removal that is still in
     flight — a list loaded before the operation was recorded. The server
     refused and committed nothing; the row's Resume action is the way on. */
  removal_in_progress:
    "{member} has a Transformation Lead removal in progress. Resume it from the row action; the role can be granted again only once it completes.",
  /* PB-5195: the sign-in service does not know a role being granted, so the
     server refused before writing anything. */
  tenant_role_not_provisioned:
    "Sign-in is not set up for this role yet. Ask your IT team to add it.",
  keycloak_unavailable: "The sign-in service is not responding. Try again.",
};

interface StatusPillSpec {
  tone: "healthy" | "critical" | "neutral";
  label: string;
}

const STATUS_PILL: Record<MembershipStatus, StatusPillSpec> = {
  active: { tone: "healthy", label: "Active" },
  suspended: { tone: "critical", label: "Suspended" },
  removed: { tone: "neutral", label: "Removed" },
};

interface SyncPillSpec {
  tone: "watch" | "critical";
  label: string;
  /** The hover explanation: what the pill means for the member's sign-in. */
  title: string;
}

/* `syncStatus` is the latest identity-sync outbox row for the member (Auth):
   waiting_identity | pending | processing | succeeded | superseded |
   dead_letter. Only the EXCEPTIONS get a pill (PB-5216, d3): the three
   in-flight states say the identity provider has not caught up with the
   roster yet, and dead_letter says it will not without help. A finished or
   superseded sync is the normal case and says nothing — as does a status this
   build does not know, because a raw token beside the email (" · succeeded")
   was the very thing this replaces. */
const SYNC_PENDING: SyncPillSpec = {
  tone: "watch",
  label: "Sync pending",
  title: "Sign-in access is being updated in the identity provider.",
};

const SYNC_PILL: Readonly<Record<string, SyncPillSpec>> = {
  waiting_identity: SYNC_PENDING,
  pending: SYNC_PENDING,
  processing: SYNC_PENDING,
  dead_letter: {
    tone: "critical",
    label: "Sync failed",
    title:
      "Sign-in access was not updated: the identity provider sync used up its retries.",
  },
};

/* PB-5294. An outstanding set-password link, and what happened to the email
   carrying it.
   EXCEPTIONS ONLY, like the sync pill above: a member with no pending token
   has either already chosen a password or predates this flow, and the row
   should claim nothing about them. What is NOT an exception is the ordinary
   "we sent it, they have not clicked yet" — that still gets a pill, because
   the whole failure this replaces is a row that looks healthy while its owner
   cannot get in.
   The three mail outcomes are kept apart deliberately: they call for
   completely different actions. `no_sender` means nobody configured a mail
   server, so nothing was even attempted and no amount of waiting helps;
   `rejected` means the transport refused a specific address; `sent` means
   wait, or re-send. Collapsing them into "email problem" is what sends an
   administrator back to the server logs. */
const SET_PASSWORD_PILL: Readonly<Record<string, SyncPillSpec>> = {
  no_sender: {
    tone: "critical",
    label: "Can't email — no mail server",
    title:
      "This person still has to choose a password, and the email could not be sent because this workspace has no mail server configured. Use Re-send to copy the link and pass it on yourself.",
  },
  rejected: {
    tone: "critical",
    label: "Password email failed",
    title:
      "This person still has to choose a password, and the mail server refused the message. Check the address, then use Re-send.",
  },
  sent: {
    tone: "watch",
    label: "Password not set",
    title:
      "The set-password email was sent and they have not chosen a password yet.",
  },
};

/* No outcome recorded yet means the message is still queued — honestly
   different from "we tried and it failed", so it must not borrow either
   failure's wording. */
/* The member owes a password but there is NO token row to describe — they were
   invited before PB-5294 shipped, when the realm's own email was supposed to
   carry them and never did. Kept apart from the queued spec above because the
   two know different amounts: that one has seen a link and is waiting on the
   outbox, this one has no record of any link at all and the administrator's
   re-send is what creates one. Said without naming an email, because no email
   is on its way. */
const SET_PASSWORD_NO_RECORD: SyncPillSpec = {
  tone: "watch",
  label: "Password not set",
  title:
    "This person still has to choose a password, and this workspace has no record of a link being sent to them. Use Re-send to create one.",
};

const SET_PASSWORD_QUEUED: SyncPillSpec = {
  tone: "watch",
  label: "Password not set",
  title:
    "This person still has to choose a password. The email is queued to go out.",
};

function setPasswordPill(
  setPassword: { readonly mailOutcome: string | null } | null | undefined,
): SyncPillSpec | null {
  if (!setPassword) return null;
  const outcome = setPassword.mailOutcome;
  if (outcome === null) return SET_PASSWORD_QUEUED;
  /* `Object.hasOwn` for the same reason the sync pill uses it: the value is
     server-written text. A `duplicate` outcome, or one this build does not
     know, still means the password is not set — so fall back to saying that
     rather than saying nothing. */
  return Object.hasOwn(SET_PASSWORD_PILL, outcome)
    ? (SET_PASSWORD_PILL[outcome] as SyncPillSpec)
    : SET_PASSWORD_QUEUED;
}

/* `Object.hasOwn`, not a bare index: the status is server-written text, and
   `SYNC_PILL["constructor"]` would otherwise resolve to a function. */
function syncPill(status: string | null): SyncPillSpec | null {
  return status !== null && Object.hasOwn(SYNC_PILL, status)
    ? (SYNC_PILL[status] as SyncPillSpec)
    : null;
}

/* Row action icons. Disabled here only ever means "request in flight" —
   permanently impossible actions are not rendered at all, because a
   permanently-greyed icon measured 1.5:1 against the light theme's white card
   and read as a rendering fault. `opacity-50` matches Button's disabled
   treatment, and the hover colour is suppressed so an in-flight Remove does
   not turn red under the cursor. */
/** The local-account dialog, stamped with the session AND the open it belongs to. */
interface LocalAccountDialogState {
  /* Unique per open, for the reason session-scope.ts sets out: a close and
     reopen inside one session yields an identical scope, so the scope alone
     cannot tell one dialog from the next.

     Unlike the invite dialog, this one CANNOT currently be dismissed mid-flight
     — `closeLocalAccount` returns early on the pending state, which gates
     Escape, the header close button and Cancel alike — so the reopen race is
     unreachable today and the id is redundant with that gate. It is kept because
     the two are independent: the gate is about not interrupting a request, this
     is about which dialog may display a credential, and relaxing the gate would
     otherwise silently turn a show-once password loose in a dialog that never
     asked for it. A test pins the gate. */
  readonly id: number;
  readonly scope: SessionScope;
  readonly email: string;
  readonly displayName: string;
  /* Two halves of one role set, mirroring the two controls (ADR-0078). */
  readonly access: BaseAccess;
  readonly lead: boolean;
  /* The created account, held so its temporary password can be copied. This is
     the only place that password exists after the response — the server keeps a
     hash — so closing the dialog loses it for good. */
  readonly created: LocalUserProvisioningResponse | null;
  readonly copied: boolean;
  /* A refused clipboard write. Held here rather than read off the creation
     mutation: the two are independent, and `createLocal.reset()` must not be
     able to erase a copy failure the admin still needs to act on. */
  readonly copyError: string | null;
}

/* What a patch may change. `id` and `scope` are omitted, not merely `readonly`:
   they are set once at open and are what the write fence compares, so a patch
   that could reassign either would be able to rewrite the identity it is being
   checked against. No caller does that today — this makes it unrepresentable. */
type LocalAccountPatch = Partial<Omit<LocalAccountDialogState, "id" | "scope">>;

/** What a local-account submission carries: the dialog and session that authored it. */
interface ScopedLocalAccountDraft {
  readonly dialogId: number;
  readonly scope: SessionScope;
  readonly email: string;
  readonly displayName: string;
  readonly roles: readonly TenantRole[];
}

/** What a directory provisioning submission carries, for the same reason. */
interface ScopedProvisionDraft {
  readonly scope: SessionScope;
  readonly directoryUserId: string;
  readonly roles: readonly TenantRole[];
}

function memberLabel(member: AdminMember | undefined): string {
  return member?.displayName ?? member?.email ?? "That member";
}

function describeAdminError(
  error: Error,
  member: AdminMember | undefined,
): string {
  /* PB-540. Keyed on the code, not the message: auth now sends both, and the
     message is its own human sentence. Anything unmapped falls back to that
     sentence, which is what the two directory/local-account alerts render. */
  const code = error instanceof AdminApiError ? error.code : undefined;
  /* Own-property check: the map is a plain literal, so a code of "constructor"
     or "toString" would otherwise resolve to an inherited function, and the
     `.replace` below would throw during render. */
  const copy =
    code !== undefined && Object.hasOwn(ADMIN_ERROR_COPY, code)
      ? ADMIN_ERROR_COPY[code]
      : undefined;
  return copy ? copy.replace("{member}", memberLabel(member)) : error.message;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? "";
  return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
}

/**
 * The delivery this member row reports on, named by its REGISTRY NOTIFICATION
 * TYPE rather than by the catalogue message key.
 *
 * `mail_deliveries.event_type` stores what the send job carried, and the send
 * path picks its message by `notificationType` — so the rows say
 * `identity.member.added`. The message rendered for it is `access_granted`, but
 * that key never reaches the delivery row and querying by it matches nothing.
 */
const NOTIFIED_EVENT_TYPE = "identity.member.added";

/**
 * What a member row says about the access-granted message, or null for nothing.
 *
 * Three states rather than two. The ticket's own scenario is an administrator
 * asking "why hasn't this person signed in?", and a rejected send is the most
 * useful answer to it — collapsing it into silence would send them to the
 * Communication page to correlate by hand, which is the friction this removes.
 *
 * A `pending` row is genuinely INDETERMINATE, not a failure: the send was
 * interrupted between submitting and settling, so whether it went out is
 * unknown. It reads as needing a look rather than as a bounce.
 *
 * SUBMISSION outcomes only (ADR-0050). "Notified" means the customer's mail
 * server accepted the message, never that it landed in an inbox — which is why
 * the copy says notified rather than delivered or received.
 *
 * File-local and non-exported: one call site, and a shared helper for a
 * three-branch switch would be a wider interface over less behaviour.
 */
function notifiedPill(
  notice: MailDeliveryNotice | undefined,
  /* Threaded in: this is a plain helper, not a component, so it cannot reach
     for the formatter itself — and the roster's dates must all share one zone. */
  date: (value: string) => string,
): { tone: "healthy" | "critical" | "watch"; label: string } | null {
  /* Absent means never sent, NOT unknown. The lookup returns a subset of what
     it was asked about, so a missing recipient is a real answer. */
  if (!notice) return null;
  const on = date(notice.submittedAt);
  switch (notice.outcome) {
    case "accepted":
      return { tone: "healthy", label: `Notified ${on}` };
    case "rejected":
      return { tone: "critical", label: `Send failed ${on}` };
    case "pending":
      /* The date matters MOST here. The outcome is indeterminate; the instant is
         not -- the claim stamped it. Without it an administrator cannot tell a
         submission stuck for three weeks from one thirty seconds old, and stuck
         is the only one of the two worth acting on. */
      return { tone: "watch", label: `Send unconfirmed ${on}` };
    default:
      /* An outcome this build does not know about. Saying nothing is the only
         safe answer: guessing could report a failure as a success. */
      return null;
  }
}

export function MembersAdmin(): ReactElement {
  const claims = useAuthStore((s) => s.claims);
  /* Advisory only — it decides WORDING, never whether the credential renders.
     Loading and failure both answer `unknown`, which takes the branch that
     claims neither a delivery nor a pause. */
  const sendingState = useMailSendingState();

  const tenantId = claims?.tenantId ?? null;
  const scope = scopeFromClaims(claims);

  const queryClient = useQueryClient();
  /* PB-4826. The policy the panel below writes, read here so the "add people"
     actions it constrains cannot offer a route that cannot work. Same query key
     as the panel, so this is a shared read rather than a second fetch.

     FAILS CLOSED, and it stays that way. Until PB-4922 (AC-16) this gate was the
     ONLY check: `provisionLocalUser` read no policy, so a local account created
     while password accounts were off was created anyway and only refused at its
     first sign-in by `completeLocalLogin`. The server now refuses it outright,
     which makes this a second line rather than the sole one — but a gate that
     opens whenever it cannot see the policy would simply walk the administrator
     through the whole dialog to earn a 403. Review caught it failing open in
     exactly the pending and error states. An affirmative `=== true` is required;
     pending and failed states say why instead. */
  const {
    policy: accessPolicy,
    isPending: accessPolicyPending,
    isError: accessPolicyFailed,
  } = useAccessPolicy();
  const localAccountsAllowed = accessPolicy?.localAccountsEnabled === true;
  /* Both switches, because an email invitation needs both: it MINTS a password
     account, so `localAccountsEnabled` gates it exactly as it gates the button
     beside it, and `localInvitationsEnabled` gates issuing new ones on top.
     `=== true` matches its sibling — a policy still loading disables the control
     and says so, rather than offering an action that may be refused. */
  const localInvitesAllowed =
    localAccountsAllowed && accessPolicy?.localInvitationsEnabled === true;
  /* Bumped by every provisioning, so the directory field gets a new source and
     drops its own 60s cache of pages (see `directory` below). */
  const [directoryGeneration, setDirectoryGeneration] = useState(0);
  /* The selection is FENCED to the tenant it was made in, the same way
     OrganizationAdmin fences its draft to the session that authored it.

     Tenant-scoping the query key stops another tenant's search RESULTS being
     served from cache, but the selection is component state and survives an
     in-place handover on its own — leaving "Add member" armed with a
     directoryUserId belonging to the tenant the admin has just left.

     Derived rather than cleared in an effect: an effect runs after render, so
     there would be one commit in which the stale selection is still live and
     submittable. Reading it through the fence makes that state unrepresentable
     instead of merely short-lived. */
  const [selectedDirectory, setSelectedDirectory] = useState<{
    readonly tenantId: string | null;
    readonly user: DirectoryUser;
  } | null>(null);
  const selectedDirectoryUser =
    selectedDirectory && selectedDirectory.tenantId === tenantId
      ? selectedDirectory.user
      : null;
  const setSelectedDirectoryUser = (user: DirectoryUser | null): void =>
    setSelectedDirectory(user ? { tenantId, user } : null);
  const [provisionAccess, setProvisionAccess] =
    useState<BaseAccess>(TENANT_MEMBER_ROLE);
  const [provisionLead, setProvisionLead] = useState(false);
  /* Owned by InvitesPanel so the toolbar and the Email invitation card open
     the same flow. */
  const { openInvite, dialog: inviteDialog } = useInviteDialog();
  /* ONE fenced state object for the whole local-account dialog, not six loose
     pieces — the shape the invite dialog uses, for the reasons session-scope.ts
     records. Every part of it belongs to the session that opened it: the draft
     address and role are that admin's intent, and `created` holds a show-once
     temporary password.

     This replaces six unfenced `useState` fields whose credential survived a
     handover only because the stamping closure happened to be stale. That was an
     accident rather than a guard, and it is one a `useMutation` refactor
     REMOVES: TanStack reads its options from the latest render, not the
     submitting one, so an `onSuccess` reading `tenantId` off the component would
     stamp the credential with the REPLACEMENT tenant and display it. The dialog
     id carried in the mutation's variables is what makes the adoption sound.

     Fenced on the full `SessionScope`, not the tenant alone: two admins of the
     same tenant can replace each other, and the second must not inherit the
     first's credential either.

     Derived rather than cleared in an effect — an effect runs after render, so
     there would be one commit in which the credential is still on screen and
     copyable. This makes that state unrepresentable instead of short-lived. */
  const [localDialog, setLocalDialog] =
    useState<LocalAccountDialogState | null>(null);
  /* A counter, not a random or time-based value: deterministic in tests, and
     monotonic per mount is all the uniqueness this needs — ids are only ever
     compared with ids from the same component instance. */
  const nextLocalDialogId = useRef(0);
  const activeLocal =
    localDialog && sameSession(localDialog.scope, scope) ? localDialog : null;
  /* Fenced for the same reason, with a different consequence: the confirmation
     names a member by display name but acts on a userId, so a handover between
     opening the dialog and confirming would REMOVE a member of the tenant the
     admin has just left — while showing them a name from the tenant they were
     looking at. */
  const [removal, setRemoval] = useState<{
    readonly tenantId: string | null;
    readonly member: AdminMember;
  } | null>(null);
  const removeTarget =
    removal && removal.tenantId === tenantId ? removal.member : null;
  const setRemoveTarget = (member: AdminMember | null): void =>
    setRemoval(member ? { tenantId, member } : null);
  const [suspension, setSuspension] = useState<{
    readonly tenantId: string | null;
    readonly member: AdminMember;
  } | null>(null);
  const suspendTarget =
    suspension && suspension.tenantId === tenantId ? suspension.member : null;
  const setSuspendTarget = (member: AdminMember | null): void =>
    setSuspension(member ? { tenantId, member } : null);
  /* The role-change confirmation (PB-5406). Fenced on the FULL `SessionScope`,
     not the tenant alone, which is what `session-scope.ts` asks for in as many
     words: two administrators of the same tenant replace one another in place
     and `/admin` never unmounts across the `/me` refetch, so a tenant-only
     stamp leaves the FIRST administrator's pending privilege grant on screen
     for the SECOND to confirm. A dialog that exists to make a privilege change
     deliberate is the last one that should be confirmable by someone who did
     not open it. The dialogs beside this one still carry the weaker stamp;
     tightening those is a separate change, not this ticket's. */
  const [roleConfirmation, setRoleConfirmation] = useState<{
    readonly scope: SessionScope;
    readonly request: RoleConfirmation;
  } | null>(null);
  const roleConfirmTarget =
    roleConfirmation && sameSession(roleConfirmation.scope, scope)
      ? roleConfirmation.request
      : null;
  /* Transformation Lead removal (ADR-0078). Fenced like `removal`, and for the
     same reason — the dialog names a member and transfers what they own, so it
     must not survive a handover into a tenant where that member is someone else. */
  const [leadRemoval, setLeadRemoval] = useState<{
    readonly tenantId: string | null;
    readonly member: AdminMember;
  } | null>(null);
  const leadRemovalTarget =
    leadRemoval && leadRemoval.tenantId === tenantId
      ? leadRemoval.member
      : null;
  /* Sign-in sessions (PB-2927). Fenced on tenantId for exactly the reason
     `removal` is: if the admin's tenant changes while the dialog is open, an
     open dialog must not keep acting on a member of the tenant they just left. */
  const [sessionsState, setSessionsState] = useState<{
    readonly tenantId: string | null;
    readonly member: AdminMember;
  } | null>(null);
  const sessionsMember =
    sessionsState && sessionsState.tenantId === tenantId
      ? sessionsState.member
      : null;
  /* Member-list filters, applied server-side. Debounced 300ms so typing doesn't
     fire a request per keystroke. The term is fenced to its tenant: it is a
     person's name or email address, so after an in-place handover an unfenced
     box would show the incoming administrator what their predecessor was
     looking for. */
  const [memberSearchState, setMemberSearchState] = useState<{
    readonly tenantId: string | null;
    readonly text: string;
  }>({ tenantId, text: "" });
  const [debouncedMemberState, setDebouncedMemberState] = useState<{
    readonly tenantId: string | null;
    readonly text: string;
  }>({ tenantId, text: "" });
  const memberSearch =
    memberSearchState.tenantId === tenantId ? memberSearchState.text : "";
  const debouncedMemberSearch =
    debouncedMemberState.tenantId === tenantId ? debouncedMemberState.text : "";
  const setMemberSearch = (text: string): void =>
    setMemberSearchState({ tenantId, text });
  const [memberSearchParams] = useSearchParams();
  /* Seeded from the URL so another screen can hand the administrator a narrowed
     roster — Identity's suspended count links here (PB-5332 D18). Read ONCE, as
     the initial value: the select stays the owner of the filter afterwards, so
     changing it does not fight the query string. */
  const [statusFilter, setStatusFilter] = useState<
    RosterMembershipStatus | "all"
  >(() => initialStatusFilter(memberSearchParams.get("status")));
  const [roleFilter, setRoleFilter] = useState<TenantRole | "all">("all");
  const formatter = useDateTimeFormatter();
  const { dateTime, dateTimeOr, date } = formatter;
  const [memberPage, setMemberPage] = useState(1);
  const [activeTab, setActiveTab] = useState("members");
  /* Switching sub-tab changes no route, so the rail's guarded navigate
     never sees it — and an inactive TabPanel renders null, so the panel
     holding the draft simply unmounts. Ask the host guard directly
     (PB-5403). */
  const confirmLeave = useConfirmLeave();
  const changeTab = (next: string): void => {
    /* Re-selecting the active tab is not leaving it, and Tabs emits for the
       selected tab too. Asking here would disarm the guard rather than
       protect the draft — same reasoning as the admin rail (review D-1). */
    if (next === activeTab) return;
    void confirmLeave().then((leave) => {
      if (leave) setActiveTab(next);
    });
  };
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  /* Stamped with the tenant the term was typed under, so a debounce still in
     flight across a handover lands in the previous tenant's slot and is fenced
     out rather than becoming the new tenant's search. */
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedMemberState({ tenantId, text: memberSearch.trim() }),
      300,
    );
    return () => clearTimeout(timer);
  }, [memberSearch, tenantId]);

  useEffect(() => {
    setMemberPage(1);
  }, [debouncedMemberSearch, roleFilter, statusFilter, tenantId]);

  const memberFilters: AdminMemberFilters = {
    ...(debouncedMemberSearch ? { query: debouncedMemberSearch } : {}),
    ...(statusFilter === "all" ? {} : { status: statusFilter }),
    ...(roleFilter === "all" ? {} : { role: roleFilter }),
  };
  /* Keys come from the api module rather than being spelled out here, so the
     tenant segment is structural and a future admin query cannot forget it
     (PB-1556). See adminMembersQueryKeyPrefix for why the tenant is in the key. */
  const membersQueryKey = adminMembersNumberedQueryKey(
    tenantId,
    memberPage,
    memberFilters,
  );
  const members = useQuery({
    queryKey: membersQueryKey,
    queryFn: ({ signal }) =>
      listAdminMembersPage(memberPage, memberFilters, signal),
    enabled: canAdministerWorkspace(claims),
  });
  useEffect(() => {
    if (!members.data || memberPage <= Math.max(1, members.data.pageCount)) {
      return;
    }
    setMemberPage(Math.max(1, members.data.pageCount));
  }, [memberPage, members.data]);
  /* The Auth endpoint owns this exclusion. Keep the client fence as well so a
     stale cached page or an older rolling-deployment replica cannot put an
     offboarded identity back into the operational roster. */
  const memberRows = (members.data?.users ?? []).filter(
    (member) => member.status !== "removed",
  );
  /* The Entra search as the directory field's source: grant mode `none`, no
     "Assign to me" and no `resolve` (an Entra id is not a platform user id).
     It runs through this page's QueryClient, so the tenant-scoped key, the 60s
     staleTime and the post-provisioning invalidation keep working, and it keeps
     every user it returns so a pick can find the whole DirectoryUser — the
     `membership` flag included.

     A NEW source per tenant and per provisioning: PeopleField caches pages per
     source for 60s, so reusing one would serve the previous tenant's people, or
     a `membership` from before the write. */
  const directory = useMemo(() => {
    const users = new Map<string, DirectoryUser>();
    const source = createTenantPeopleSource({
      search: async (query) => {
        /* The directory refuses fewer than three characters — and counts the
           refusal against the administrator's search allowance. */
        if (query.trim().length < 3) return { items: [], nextCursor: null };
        const found = await queryClient.fetchQuery({
          queryKey: adminDirectoryQueryKey(tenantId, query),
          queryFn: () => searchDirectoryUsers(query),
          staleTime: 60_000,
        });
        for (const user of found) users.set(user.directoryUserId, user);
        return {
          items: found.map((user) => ({
            id: user.directoryUserId,
            name: user.displayName,
            email: user.email ?? user.userPrincipalName,
          })),
          // searchDirectoryUsers reads one page and takes no cursor.
          nextCursor: null,
        };
      },
    });
    return { users, source };
  }, [queryClient, tenantId, directoryGeneration]);

  /* Whether each loaded member was told they have access.
     Read from the NOTIFICATION service directly rather than as a field on the
     members API: delivery results are notification's to own (ADR-0050), and
     adding `notifiedAt` to auth would widen the byte-frozen `auth.yaml` for a
     fact auth has no business holding.

     Sorted so the key is canonical — the same set loaded in a different order
     must not be a second cache entry. The tenant is in the key for the reason
     every other admin key has it (PB-1556): a session swap inside a mounted
     /admin tree must not serve one tenant's data to the next. */
  const notifiedRecipientIds = [...memberRows].map((m) => m.userId).sort();
  const notified = useQuery({
    queryKey: [
      "admin",
      "member-notified",
      tenantId,
      NOTIFIED_EVENT_TYPE,
      notifiedRecipientIds,
    ],
    queryFn: () =>
      lookupMailDeliveries(NOTIFIED_EVENT_TYPE, notifiedRecipientIds),
    enabled: canAdministerWorkspace(claims) && notifiedRecipientIds.length > 0,
    staleTime: 60_000,
  });
  /* Keyed by recipient so a row is a map lookup rather than a scan per render.
     `undefined` for a member the response omitted, which `notifiedPill` reads
     as "never sent".

     DELIBERATELY EMPTY WHILE ERRORED. TanStack keeps the last successful `data`
     when a refetch fails, so without this guard a failed refresh would render
     the previous answer's pills NEXT TO the notice saying the status could not
     be loaded — two contradictory claims at once, one of them possibly stale.
     Dropping to empty makes the notice the single answer on screen, and the
     notice is what explains that a missing pill currently means nothing. */
  const notifiedByUserId = new Map(
    (notified.isError ? [] : (notified.data ?? [])).map((notice) => [
      notice.recipientUserId,
      notice,
    ]),
  );
  /* An absent pill MEANS "never sent", so the page may only stay silent when it
     actually knows. It does not know in two states, not one: an outright failure,
     and the first round trip before any answer has arrived. The error path was
     guarded and the loading path was not, so during the initial fetch every row
     quietly asserted something the page had not yet been told.
     `notified.data` rather than `isPending` on purpose — a background refetch
     over a known answer keeps showing that answer instead of flickering. */
  const notifiedStatusUnknown =
    notifiedRecipientIds.length > 0 &&
    (notified.isError || notified.data === undefined);
  /* Prefix key, not the full one: a mutation must refresh the member list for
     every filter combination cached, not just the one on screen.

     The tenant is a PARAMETER rather than the render's `tenantId` because a
     refresh belongs to the tenant that RAISED the mutation. TanStack calls the
     LATEST `onSuccess`, so a callback closing over the render would invalidate
     whichever tenant the admin has moved to and leave the initiating tenant's
     list stale — precisely the cross-tenant cache confusion the scoped keys
     exist to prevent, reintroduced one layer up. Every mutation below therefore
     carries its originating tenant in its own variables, the way
     OrganizationAdmin carries its originating session. */
  const refreshMembers = (originTenantId: string | null): Promise<void> =>
    queryClient.invalidateQueries({
      queryKey: adminMembersQueryKeyPrefix(originTenantId),
    });
  const provision = useMutation({
    /* Variables, not component closure. `useMutation` reads its options from the
       LATEST render rather than the submitting one, so a `mutationFn` closing
       over `selectedDirectoryUser` submits whatever happens to be selected when
       the request starts — and needed a non-null assertion to compile, which the
       variables form makes unnecessary rather than merely hidden. */
    mutationFn: ({ directoryUserId, roles }: ScopedProvisionDraft) =>
      provisionDirectoryUser(directoryUserId, roles),
    onSuccess: async (_result, variables) => {
      /* Clearing the form is the SESSION's own, so it is fenced: after an
         in-place handover the selection is the NEW administrator's pick, and
         wiping it would delete their work to tidy up after someone else's. Read
         the live snapshot rather than a render closure so the check cannot lag
         the handover by a commit. */
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, live)) setSelectedDirectoryUser(null);
      /* The write is COMMITTED and belongs to the tenant, not to the session that
         submitted it, so both caches are refreshed either way — for the tenant
         that SUBMITTED it, read off the variables. Once the keys carry a tenant
         (PB-1556) an unqualified refresh is no longer tenant-agnostic: taken off
         the render it would name whichever tenant the admin has moved to, and
         leave the submitting tenant's list stale.

         The directory results carry a `membership` flag that drives the "already
         a member" line and disables Add member. Provisioning changes that flag
         server-side, so a cached result set from before the write would offer to
         add the same person again — the 60s staleTime makes that window real.
         Prefix key, so every cached query text is refreshed, not just this one;
         and a new source, so the field's own page cache goes with it. */
      const origin = variables.scope?.tenantId ?? null;
      setDirectoryGeneration((generation) => generation + 1);
      await Promise.all([
        refreshMembers(origin),
        queryClient.invalidateQueries({
          queryKey: adminDirectoryQueryKeyPrefix(origin),
        }),
      ]);
    },
  });
  /* A mutation's `isPending` and `error` belong to the component, not to the
     session that started it — the same reasoning as OrganizationAdmin's save and
     the invite dialog's create. Without this, a provisioning request begun by the
     previous session disables the next administrator's Add member button and
     reports its failure over a form they never used. */
  const provisionIsOurs =
    provision.variables !== undefined &&
    sameSession(provision.variables.scope, scope);
  const provisioning = provision.isPending && provisionIsOurs;
  const provisionError = provisionIsOurs ? provision.error : null;
  /* PB-5406, the second half: no member action said anything when it worked.
     Role change, Transformation Lead grant, suspend and reactivate all updated
     the row in silence, so an administrator could not tell a completed write
     from one that never left the browser.

     Fenced on the FULL session against a live snapshot, not a render closure,
     for the reason every write on this page is: `/admin` does not unmount
     across a `/me` refetch, so a response that outlived a handover would
     otherwise announce, by name, a member of the tenant its administrator has
     just left. Silence is the correct output there — the new administrator
     did nothing and the old one is gone.

     Only the writes with no other success surface are announced. Re-sending a
     set-password link, resetting a password, retrying a sign-out and creating
     a local account each already show their own outcome, and a second
     notification for those would be noise. */
  const announceMemberChange = (
    change: MemberChange,
    member: AdminMember,
    at: SessionScope,
  ): void => {
    if (!sameSession(at, scopeFromClaims(useAuthStore.getState().claims))) {
      return;
    }
    toast(MEMBER_CHANGE_MESSAGE[change](memberLabel(member)), {
      tone: "healthy",
    });
  };
  const changeRole = useMutation({
    /* `member` and `change` ride along so the outcome can be announced from
       the variables rather than looked up afterwards (PB-5406): a refetch
       between the click and the response makes a roster lookup miss, which is
       the same trap `resendSetPassword` documents below. */
    mutationFn: ({
      userId,
      roles,
    }: {
      userId: string;
      roles: readonly TenantRole[];
      member: AdminMember;
      change: MemberChange;
      scope: SessionScope;
    }) => updateTenantRoles(userId, roles),
    onSuccess: (_result, variables) => {
      announceMemberChange(variables.change, variables.member, variables.scope);
      return refreshMembers(variables.scope?.tenantId ?? null);
    },
  });
  /* The staged cross-service removal (ADR-0078). Its pending and error state
     live in the DIALOG, which owns the idempotency key and the retry; this
     mutation only refreshes what the completed operation changed — the member
     list (role gone) and that member's ownership counts (now zero). Both keyed on
     the tenant that RAISED it, like every mutation above. */
  const removeLead = useMutation({
    mutationFn: ({
      userId,
      input,
    }: {
      userId: string;
      input: TransformationLeadRemovalInput;
      member: AdminMember;
      scope: SessionScope;
    }) => removeTransformationLead(userId, input),
    onSuccess: async (_result, variables) => {
      /* The dialog shows the transfer in detail and then CLOSES, which left
         nothing on screen saying the staged removal actually completed. */
      announceMemberChange("lead-removed", variables.member, variables.scope);
      const origin = variables.scope?.tenantId ?? null;
      await Promise.all([
        refreshMembers(origin),
        queryClient.invalidateQueries({
          queryKey: adminLamaOwnershipQueryKey(origin, variables.userId),
        }),
      ]);
    },
  });
  /* PB-5345 review run 8 (finding 3). THE DURABLE HALF OF THE SIGN-OUT RETRY.
     The dialog below also offers it, but that dialog lives in browser memory:
     closing it or reloading lost the only route that closes a recorded stage,
     and the member kept the live sessions the reset was supposed to end. The
     server marks the row, so the row can offer it — the same shape the
     Transformation Lead removal already uses for its Resume.

     Keyed on the MEMBER, not a local dialog id, because that is what survives
     a reload and what the endpoint takes. The outcome is kept per member for
     the same reason: a retry that did not finish must say so on the row it
     belongs to, not on whichever row was touched last. */
  const [signOutRetryRowOutcome, setSignOutRetryRowOutcome] = useState<{
    readonly userId: string;
    readonly outcome: "revoked" | "failed";
  } | null>(null);
  const finishResetSignOut = useMutation({
    mutationFn: ({ userId }: { userId: string; scope: SessionScope }) =>
      retryPasswordResetSignOut(userId),
    onSuccess: async (result, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, live)) {
        /* The server's answer, not an assumption. `nothing_open` means somebody
           else already closed it, which is as good as done from here. */
        setSignOutRetryRowOutcome({
          userId: variables.userId,
          outcome: result.outcome === "still_incomplete" ? "failed" : "revoked",
        });
      }
      /* The server-side marker changed for the originating tenant even if the
         administrator handed the session over while this request was in flight. */
      await refreshMembers(variables.scope?.tenantId ?? null);
    },
    onError: (_error, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (!sameSession(variables.scope, live)) return;
      setSignOutRetryRowOutcome({
        userId: variables.userId,
        outcome: "failed",
      });
    },
  });
  const changeStatus = useMutation({
    /* Member-status writes for one tenant are serialized. Without a shared
       mutation scope, two removals can overlap: the earlier failure then
       restores its full snapshot and resurrects the later successful removal. */
    scope: { id: `member-status:${scope?.tenantId ?? "none"}` },
    mutationFn: ({
      userId,
      status,
    }: {
      userId: string;
      status: "active" | "suspended" | "removed";
      member: AdminMember;
      scope: SessionScope;
    }) => updateMembershipStatus(userId, status),
    /* Removal is the one status change whose success means the row no longer
       belongs in any visible member result. Waiting for the follow-up GET kept
       a successfully removed person on screen until a manual refresh whenever
       that request was delayed or an intermediary returned its previous page.
       Remove the row from every cached filter/page for the originating tenant
       before the request starts, then reconcile all of them with the server.

       Keep exact page snapshots for rollback. They are tenant-scoped by the
       same prefix as the reads, so a failed mutation cannot restore one
       tenant's roster into another tenant after an in-place handover. */
    onMutate: async (variables) => {
      if (variables.status !== "removed") return undefined;

      const queryKey = adminMembersQueryKeyPrefix(
        variables.scope?.tenantId ?? null,
      );
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient
        .getQueriesData<AdminMemberNumberedPage>({ queryKey })
        .filter(
          ([cachedKey, page]) =>
            Array.isArray(cachedKey) &&
            cachedKey.at(-2) === "numbered" &&
            page !== undefined,
        );
      /* A member may be present on only one cached page in a filtered result,
         but totalCount/pageCount describe the whole filtered page set. Mark
         every filter group that contains the member, then update metadata on
         all cached pages in that group while removing the row only where it
         actually appears. */
      const affectedGroups = new Set(
        snapshots
          .filter(([, page]) =>
            page?.users.some((member) => member.userId === variables.userId),
          )
          .map(([cachedKey]) => JSON.stringify(cachedKey.slice(0, -2))),
      );

      for (const [cachedKey, page] of snapshots) {
        if (
          !page ||
          !affectedGroups.has(JSON.stringify(cachedKey.slice(0, -2)))
        ) {
          continue;
        }
        const totalCount = Math.max(0, page.totalCount - 1);
        queryClient.setQueryData<AdminMemberNumberedPage>(cachedKey, {
          ...page,
          users: page.users.filter(
            (member) => member.userId !== variables.userId,
          ),
          totalCount,
          pageCount: Math.max(1, Math.ceil(totalCount / page.pageSize)),
        });
      }

      return { snapshots };
    },
    onError: (_error, variables, context) => {
      if (variables.status !== "removed") return;
      for (const [queryKey, page] of context?.snapshots ?? []) {
        queryClient.setQueryData(queryKey, page);
      }
    },
    /* Removal is announced like the rest even though its row disappears: the
       optimistic `onMutate` above takes the row out BEFORE the server answers,
       so a vanished row is not yet evidence that anything was written. */
    onSuccess: (_result, variables) =>
      announceMemberChange(
        MEMBER_STATUS_CHANGE[variables.status],
        variables.member,
        variables.scope,
      ),
    onSettled: (_result, _error, variables) =>
      refreshMembers(variables.scope?.tenantId ?? null),
  });
  /* PB-5294 (AC-7). Mint a fresh link and enqueue its email.
     Session-fenced like every other mutation on this page: the scope comes
     off `variables`, never a render closure, so a tenant handover mid-flight
     cannot refresh the wrong roster — or, far worse here, show one
     administrator a credential link minted for another workspace.

     The link is STAMPED with the session that minted it, the way the
     local-account dialog stamps its temporary password, because the sentence
     above was a CLAIM and not yet a guard: `/admin` never unmounts across a
     `/me` refetch, so without the stamp a live set-password URL stays on
     screen — one click from the replacement administrator's clipboard — for a
     member of the tenant they have just left. Fenced on the full
     `SessionScope` and not the tenant alone: two admins of one tenant can
     replace each other, and the second must not inherit the first's
     credential either. */
  const [setPasswordLink, setSetPasswordLink] = useState<{
    readonly operationId: number;
    readonly scope: SessionScope;
    readonly member: AdminMember;
    readonly url: string;
    readonly expiresAt: string;
  } | null>(null);
  /* The response is handed over by ref instead of returned, and that is a
     credential-lifetime decision rather than a style one — the same one
     `createLocal` below and `InvitesPanel`'s `create` both record. Whatever a
     `mutationFn` RETURNS becomes `Mutation.state.data` in the shared
     QueryClient's MutationCache, which `reset()` does not reach, so returning
     the URL parked a second copy of a LIVE credential somewhere this component
     cannot retire — readable after the modal closes and after a handover, and
     falsifying this surface's own claim that the link lives in component state
     only. See docs/learnings/2026-08-12-mutation-cache-holds-show-once-
     credentials.md; this is the third instance.

     Keyed per click rather than held in one slot, because two re-sends
     genuinely overlap: the row's disabled state tracks only the LATEST
     `variables`, so a second row is clickable while the first is in flight and
     a single slot would let the later response overwrite the earlier one.
     Every terminal path clears its own key, so the map stays bounded. */
  const nextResendId = useRef(0);
  const deliveredSetPasswordLinks = useRef(
    new Map<
      number,
      { readonly expiresAt: string; readonly setPasswordUrl: string }
    >(),
  );
  const resendSetPassword = useMutation({
    mutationFn: async ({
      operationId,
      userId,
    }: {
      operationId: number;
      userId: string;
      member: AdminMember;
      scope: SessionScope;
    }): Promise<void> => {
      deliveredSetPasswordLinks.current.set(
        operationId,
        await resendSetPasswordEmail(userId),
      );
    },
    onSuccess: (_result, variables) => {
      /* Read and removed together, so the slot never outlives the commit that
         consumes it. The undefined branch is unreachable — `mutationFn` writes
         this key before it resolves — and narrows the type. */
      const issued = deliveredSetPasswordLinks.current.get(
        variables.operationId,
      );
      deliveredSetPasswordLinks.current.delete(variables.operationId);
      /* The WRITE half of the fence, beside the render half below —
         `session-scope.ts` is explicit that neither can do the other's job.
         `useMutation` runs the LATEST render's `onSuccess`, so a response that
         outlived its session would otherwise DISPLACE a link the live
         administrator is holding, and theirs is show-once too: the render
         fence would then drop the stale write and leave them with nothing for
         a link the server has already minted. Live snapshot rather than a
         render closure, exactly like `provision` above, so the check cannot lag
         the handover by a commit. */
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (issued && sameSession(variables.scope, live)) {
        setSetPasswordLinkCopiedFor(null);
        setSetPasswordLinkCopyError(null);
        setSetPasswordLink({
          operationId: variables.operationId,
          scope: variables.scope,
          /* From `variables`, not `members.items.find(...)`: the roster is the
             LATEST render, so a refetch or a filter change between the click
             and the response would make the lookup miss and drop a show-once
             credential with no message at all. */
          member: variables.member,
          url: issued.setPasswordUrl,
          expiresAt: issued.expiresAt,
        });
      }
      void refreshMembers(variables.scope?.tenantId ?? null);
    },
    onError: (_error, variables) => {
      /* A failed request writes no entry, so this clears nothing today. It is
         here because the map must not depend on which terminal path a request
         takes in order to stay bounded — every operation removes its own key. */
      deliveredSetPasswordLinks.current.delete(variables.operationId);
    },
  });
  /* The RENDER half. Derived, not cleared in an effect: an effect runs after
     render, so there would be one commit in which a retired session's link is
     still on screen and copyable — the same reason `activeLocal` is derived.
     The modal's `open` AND its whole body read this, so a handover empties the
     panel in the commit the session changes, before the shell's exit
     transition has even started. */
  const activeSetPasswordLink =
    setPasswordLink && sameSession(setPasswordLink.scope, scope)
      ? setPasswordLink
      : null;
  /* Keyed by the operation they describe rather than held as bare flags, for
     the same reason the link itself is fenced. `writeText` is asynchronous and
     the administrator can Done one link and re-send for a DIFFERENT member
     while it is still settling; a boolean would then badge the new link
     "Copied" when nothing of it ever reached the clipboard — the exact lie
     `InvitesPanel` and the local-account dialog both document. Comparing ids
     makes the outcome describe its own link or nothing. */
  const [setPasswordLinkCopiedFor, setSetPasswordLinkCopiedFor] = useState<
    number | null
  >(null);
  const [setPasswordLinkCopyError, setSetPasswordLinkCopyError] = useState<{
    readonly operationId: number;
    readonly message: string;
  } | null>(null);
  const setPasswordLinkCopied =
    activeSetPasswordLink !== null &&
    setPasswordLinkCopiedFor === activeSetPasswordLink.operationId;
  const setPasswordLinkCopyErrorText =
    activeSetPasswordLink !== null &&
    setPasswordLinkCopyError?.operationId === activeSetPasswordLink.operationId
      ? setPasswordLinkCopyError.message
      : null;
  const copySetPasswordLink = async (): Promise<void> => {
    if (!activeSetPasswordLink) return;
    const { operationId, url } = activeSetPasswordLink;
    try {
      await navigator.clipboard.writeText(url);
      setSetPasswordLinkCopiedFor(operationId);
      setSetPasswordLinkCopyError(null);
    } catch {
      /* Clipboard access is refused outright in some browsers and over
         plain HTTP. Saying so beats a button that silently does nothing,
         because the link is still selectable by hand. */
      setSetPasswordLinkCopiedFor(null);
      setSetPasswordLinkCopyError({
        operationId,
        message: "The link could not be copied. Select and copy it manually.",
      });
    }
  };
  /* PB-5345. The administrator reset, and the same three rules the re-send
     above already follows, for the same reasons.

     STAMPED WITH ITS SESSION, because `/admin` never unmounts across a `/me`
     refetch: without it a live password would sit on screen, copyable, after a
     handover put a different administrator in front of it.

     HANDED OVER BY REF rather than returned, because whatever a `mutationFn`
     returns becomes `Mutation.state.data` in the shared MutationCache, which
     `reset()` does not reach — a second copy of a live credential this
     component could not retire. Fourth instance of that rule.

     KEYED PER CLICK, so two resets that overlap cannot have the later response
     overwrite the earlier one in a single slot.

     What it adds is a CONFIRM step, which the re-send does not need: a re-send
     mints a link for somebody who has no password, while this replaces one that
     works and ends their sessions. `issued === null` is the confirm step. */
  const [passwordReset, setPasswordReset] = useState<{
    readonly operationId: number;
    readonly scope: SessionScope;
    readonly member: AdminMember;
    readonly issued: MemberPasswordResetResponse | null;
  } | null>(null);
  const nextPasswordResetId = useRef(0);
  const deliveredPasswords = useRef(
    new Map<number, MemberPasswordResetResponse>(),
  );
  const resetPassword = useMutation({
    mutationFn: async ({
      operationId,
      userId,
    }: {
      operationId: number;
      userId: string;
      member: AdminMember;
      scope: SessionScope;
    }): Promise<void> => {
      deliveredPasswords.current.set(
        operationId,
        await resetMemberPassword(userId),
      );
    },
    onSuccess: (_result, variables) => {
      const issued = deliveredPasswords.current.get(variables.operationId);
      deliveredPasswords.current.delete(variables.operationId);
      /* Live snapshot rather than a render closure, exactly like the re-send:
         `useMutation` runs the LATEST render's `onSuccess`, so a response that
         outlived its session must be dropped rather than displace what the
         current administrator is holding. */
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (issued && sameSession(variables.scope, live)) {
        setPasswordResetCopiedFor(null);
        setPasswordResetCopyError(null);
        /* Only into the dialog this response BELONGS to. The close gate above
           makes a mismatch unreachable today — the dialog cannot be dismissed
           while its own request is in flight — but the two fences answer
           different questions, and an unconditional write here is what would
           put one member's password in front of another member's name if that
           gate were ever relaxed. */
        setPasswordReset((current) =>
          current && current.operationId === variables.operationId
            ? {
                ...current,
                /* From `variables`, not a roster lookup: a refetch between the
                   click and the response would miss and drop a show-once
                   credential. */
                member: variables.member,
                issued,
              }
            : current,
        );
      }
      void refreshMembers(variables.scope?.tenantId ?? null);
    },
    onError: (_error, variables) => {
      deliveredPasswords.current.delete(variables.operationId);
      /* Back to the confirm step rather than closed, so the refusal renders
         beside the button that caused it and the administrator can retry
         without finding the row again. */
      setPasswordReset((current) =>
        current && current.operationId === variables.operationId
          ? { ...current, issued: null }
          : current,
      );
    },
  });
  /* Derived, not cleared in an effect — an effect runs after render, so there
     would be one commit in which a retired session's password is still on
     screen and copyable. */
  const activePasswordReset =
    passwordReset && sameSession(passwordReset.scope, scope)
      ? passwordReset
      : null;
  const [passwordResetCopiedFor, setPasswordResetCopiedFor] = useState<
    number | null
  >(null);
  const [passwordResetCopyError, setPasswordResetCopyError] = useState<{
    readonly operationId: number;
    readonly message: string;
  } | null>(null);
  /* PB-5345 review run 4 (finding E). A sign-out that did not complete used to
     be reported and left there, so a reset could finish with the member still
     signed in somewhere and nothing but prose telling the administrator to go
     and look. The remedy already existed as its own endpoint — this wires it to
     the moment the problem is visible, so the partial revocation is recoverable
     rather than merely disclosed. Keyed on the operation for the same reason
     everything else in this dialog is. */
  const [signOutRetry, setSignOutRetry] = useState<{
    readonly operationId: number;
    readonly outcome: "revoked" | "failed";
  } | null>(null);
  const retrySignOut = useMutation({
    mutationFn: async ({
      userId,
    }: {
      operationId: number;
      userId: string;
      scope: SessionScope;
    }) => retryPasswordResetSignOut(userId),
    onSuccess: async (result, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, live)) {
        /* The server's answer, not an assumption: a retry that did not finish
           must not report that it did. `nothing_open` means somebody else already
           closed it, which is as good as done from here. */
        setSignOutRetry({
          operationId: variables.operationId,
          outcome: result.outcome === "still_incomplete" ? "failed" : "revoked",
        });
      }
      await refreshMembers(variables.scope?.tenantId ?? null);
    },
    onError: (_error, variables) => {
      const live = scopeFromClaims(useAuthStore.getState().claims);
      if (!sameSession(variables.scope, live)) return;
      setSignOutRetry({
        operationId: variables.operationId,
        outcome: "failed",
      });
    },
  });
  /* Per-row, per-session state for the five row actions. The shared
     `useMutation` observer is latest-only, so acting on a second row used to
     unlock the first mid-flight and drop its failure — see useRowMutation.

     `member` rides along in the variables of the actions that carry it, so the
     copy names the right person even when a refetch between the click and the
     response makes a roster lookup miss (PB-5406). */
  const describeRowError = (
    error: unknown,
    variables: { readonly userId: string; readonly member?: AdminMember },
  ): string => {
    const member =
      variables.member ??
      memberRows.find((row) => row.userId === variables.userId);
    return error instanceof Error
      ? describeAdminError(error, member)
      : `${memberLabel(member)} could not be updated.`;
  };
  const roleRow = useRowMutation(changeRole, {
    scope,
    describeError: describeRowError,
  });
  const statusRow = useRowMutation(changeStatus, {
    scope,
    describeError: describeRowError,
  });
  const resendRow = useRowMutation(resendSetPassword, {
    scope,
    describeError: describeRowError,
  });
  /* Lock only: a refused finish-sign-out already raises its own critical pill
     on the row, so this controller exists for the per-row, per-session pending
     state and not to add a second sentence saying the same thing. */
  const finishSignOutRow = useRowMutation(finishResetSignOut, {
    scope,
    describeError: describeRowError,
  });
  /* Pending only. This one is submitted from the dialog, where its refusal
     already renders (see `passwordResetErrorText` below), so a second copy on
     the row would say the same thing twice. */
  const resetPasswordRow = useRowMutation(resetPassword, {
    scope,
    describeError: describeRowError,
  });
  /* Keyed on the operation, like the copy state below and the success write
     above (CodeRabbit, PR #275). `isPending` is mutation-wide, and this page
     stays mounted across a session change: a request left in flight by a
     retired session would otherwise label a freshly opened dialog
     "Resetting..." and refuse to let it be cancelled, for an operation that had
     not been submitted.

     Deliberately NOT derived from `resetPasswordRow` (CodeRabbit, PR #361).
     The two gates answer different questions and need different keys. The ROW
     button asks "may this member's reset be started", which is per member. The
     DIALOG asks "is THIS reset running, and did THIS reset fail", which is per
     operation: close a dialog and reopen it for the same member in the same
     session, and a member-keyed error would carry the previous operation's
     refusal into the new dialog. Widening the key here to match the row would
     lose that, which is the thing PR #275 added it for. */
  const passwordResetPending =
    activePasswordReset !== null &&
    resetPassword.isPending &&
    resetPassword.variables?.operationId === activePasswordReset.operationId;
  const passwordResetCopied =
    activePasswordReset !== null &&
    passwordResetCopiedFor === activePasswordReset.operationId;
  const passwordResetCopyErrorText =
    activePasswordReset !== null &&
    passwordResetCopyError?.operationId === activePasswordReset.operationId
      ? passwordResetCopyError.message
      : null;
  const signOutRetryOutcome =
    activePasswordReset !== null &&
    signOutRetry?.operationId === activePasswordReset.operationId
      ? signOutRetry.outcome
      : null;
  const signOutRetryPending =
    activePasswordReset !== null &&
    retrySignOut.isPending &&
    retrySignOut.variables?.operationId === activePasswordReset.operationId;
  /* CodeRabbit, PR #275. `onError` returns the dialog to its confirm step, and
     nothing rendered `resetPassword.error` — so a refused reset (`awaits_password`,
     `local_accounts_disabled`, a policy rejection, Keycloak unreachable) did
     nothing at all under a button that simply re-enabled itself. That is the
     defect PB-5294 fixed for the re-send, repeated here.

     It belongs in the DIALOG rather than the row alert the other actions share:
     the dialog is modal and open at the moment of failure, so an alert behind
     it cannot be seen. Keyed on `operationId` for the same reason the copy
     state is — a refusal must describe its own reset or nothing. */
  const passwordResetErrorText =
    activePasswordReset !== null &&
    resetPassword.variables?.operationId === activePasswordReset.operationId &&
    resetPassword.error
      ? describeAdminError(resetPassword.error, activePasswordReset.member)
      : null;
  const copyTemporaryPasswordReset = async (): Promise<void> => {
    if (!activePasswordReset?.issued) return;
    const { operationId, issued } = activePasswordReset;
    try {
      await navigator.clipboard.writeText(issued.temporaryPassword);
      setPasswordResetCopiedFor(operationId);
      setPasswordResetCopyError(null);
    } catch {
      setPasswordResetCopiedFor(null);
      setPasswordResetCopyError({
        operationId,
        message:
          "The password could not be copied. Select and copy it manually.",
      });
    }
  };

  /* Synchronous callers only — each runs inside an event handler while the dialog
     is rendering, which already proves the scope matches. Anything writing AFTER
     an await goes through `patchLocalOperation`. */
  const patchLocal = (changes: LocalAccountPatch): void =>
    setLocalDialog((current) =>
      current ? { ...current, ...changes } : current,
    );

  /* The write half of the fence: applies `changes` only if the dialog on screen
     is still the one `origin` names. Every asynchronous continuation goes through
     this — the creation completion and both clipboard continuations — so a late
     result addresses its own operation or nothing at all.

     Today the SCOPE comparison is the half that does the work, because the
     pending gate on `closeLocalAccount` makes a close-and-reopen mid-flight
     unreachable; see the `id` field for why the id is carried anyway. Deleting a
     "redundant" credential guard on the strength of a passing test run is what
     cost the invite dialog three review rounds. */
  const patchLocalOperation = (
    origin: { readonly dialogId: number; readonly scope: SessionScope },
    changes: LocalAccountPatch,
  ): void =>
    setLocalDialog((current) =>
      current &&
      current.id === origin.dialogId &&
      sameSession(current.scope, origin.scope)
        ? { ...current, ...changes }
        : current,
    );

  /* The response is handed over by ref instead of being returned, and that is a
     credential-lifetime decision rather than a style one.

     Whatever a `mutationFn` returns becomes `Mutation.state.data` in TanStack's
     MutationCache, and `createLocal.reset()` does not reach it: reset detaches
     the observer, it does not erase the mutation, which then sits unobserved
     until the cache's GC window elapses. The show-once password would therefore
     outlive both the dialog and the session — readable from the shared
     QueryClient long after Done — which contradicts the invariant this whole
     fence rests on, that the dialog container is the only place the password
     exists after the response.

     Keyed by the submitting dialog rather than held in one slot, because two
     creations can genuinely overlap: a handover does not cancel the previous
     administrator's request, and the replacement can start their own before it
     settles. Both `mutationFn`s then resume before either `onSuccess` runs, so a
     single slot would let the later response overwrite the earlier one, the
     dialog-id fence would reject the mismatched pair, and the second
     administrator would be left with no password for an account the server had
     already created. Each entry is read and deleted by the operation that wrote
     it, and every terminal state clears its own key. */
  const deliveredCredentials = useRef(
    new Map<number, LocalUserProvisioningResponse>(),
  );
  const createLocal = useMutation({
    mutationFn: async ({
      dialogId,
      displayName,
      email,
      roles,
    }: ScopedLocalAccountDraft): Promise<void> => {
      deliveredCredentials.current.set(
        dialogId,
        await provisionLocalUser(displayName, email, roles),
      );
    },
    onSuccess: (_result, variables) => {
      /* Read and removed together, so the slot never outlives the commit that
         consumes it. The undefined branch is unreachable — `mutationFn` writes
         this key before it resolves — and narrows the type rather than guarding
         anything. */
      const created = deliveredCredentials.current.get(variables.dialogId);
      deliveredCredentials.current.delete(variables.dialogId);
      /* Adopted ONLY by the dialog that submitted it. Losing the password is
         recoverable — delete the account and create it again — whereas showing it
         to the wrong dialog is not. The reachable wrong dialog is a replacement
         administrator's, which the scope half of the fence catches; see the id
         field for why the id half is kept alongside it. */
      if (created) {
        patchLocalOperation(variables, {
          created,
          copied: false,
          copyError: null,
        });
      }
      /* Deliberately NOT awaited, which is the whole reason this stays a
         fire-and-forget call rather than moving into the mutation's own chain:
         awaiting would hold `isPending` open across the refresh — disabling Done
         while a show-once credential is on screen — and a refresh failure would
         replace a creation that actually succeeded. A briefly stale member list
         is the lesser problem; the credential is the part that cannot be
         re-read.

         The tenant comes off the variables for the reason this mutation exists in
         this form at all: as a `useMutation` it reads its options from the latest
         render, so a refresh taken off the component would name the replacement
         administrator's tenant and leave the creating tenant's list short a
         member it has just gained. */
      void refreshMembers(variables.scope?.tenantId ?? null).catch(
        () => undefined,
      );
    },
    onError: (_error, variables) => {
      /* A failed request writes no entry, so this clears nothing today. It is
         here because the map must not depend on which terminal path a request
         takes to stay bounded — every operation removes its own key. */
      deliveredCredentials.current.delete(variables.dialogId);
    },
  });
  /* Fenced for the same reason as `provision` above. */
  const localIsOurs =
    createLocal.variables !== undefined &&
    sameSession(createLocal.variables.scope, scope);
  const localPending = createLocal.isPending && localIsOurs;
  const localCreateError = localIsOurs ? createLocal.error : null;
  /* A refused copy takes precedence over a creation failure — it is the more
     recent thing the admin did. In practice they are mutually exclusive: copying
     is only reachable once creation has succeeded.

     `||`, not `??`, on the message: an empty string is not nullish, and the API
     client passes one straight through — `body?.message ?? body?.code ?? …`
     keeps a server body of `{"message":""}` as an empty `AdminApiError.message`.
     With `??` that renders as falsy and the whole `role="alert"` paragraph
     disappears, so the creation would fail silently in the one dialog where a
     silent failure costs an account nobody can sign in to. */
  const localErrorText =
    activeLocal?.copyError ??
    (localCreateError
      ? localCreateError.message || "Local account creation failed"
      : null);

  const openLocalAccount = (): void => {
    nextLocalDialogId.current += 1;
    setLocalDialog({
      id: nextLocalDialogId.current,
      scope,
      displayName: "",
      email: "",
      access: TENANT_MEMBER_ROLE,
      lead: false,
      created: null,
      copied: false,
      copyError: null,
    });
    createLocal.reset();
  };

  const closeLocalAccount = (): void => {
    if (localPending) return;
    setLocalDialog(null);
    createLocal.reset();
  };

  const submitLocalAccount = (): void => {
    if (!activeLocal || localPending) return;
    createLocal.mutate({
      dialogId: activeLocal.id,
      scope: activeLocal.scope,
      displayName: activeLocal.displayName.trim(),
      /* Trimmed to match the submit guard below, which tests
         `localEmail.trim()`. Not reachable through the form today — the field is
         `type="email"`, and that state's value sanitization strips leading and
         trailing whitespace before React ever sees it (proven by the test that
         asserts the field's type). It matters because nothing downstream trims:
         auth's parseLocalProvision only type-checks the string, so a padded
         value would become a Keycloak identifier. One call, and the payload no
         longer depends on an HTML sanitization subtlety staying true. */
      email: activeLocal.email.trim(),
      roles: composeTenantRoles(activeLocal.access, activeLocal.lead),
    });
  };

  const copyTemporaryPassword = async (): Promise<void> => {
    if (!activeLocal?.created) return;
    /* The dialog whose password actually reaches the clipboard, named so the
       continuation reports against it rather than against whatever is on screen
       when the write lands — a permission prompt is delay enough. Badging a
       different dialog "Copied" is a lie about which credential the admin is
       holding, and they can paste the wrong one. */
    const origin = { dialogId: activeLocal.id, scope: activeLocal.scope };
    const password = activeLocal.created.temporaryPassword;
    try {
      await navigator.clipboard.writeText(password);
      patchLocalOperation(origin, { copied: true, copyError: null });
    } catch {
      /* Clipboard access can be refused (insecure context, denied permission).
         The password is on screen and selectable, so this is recoverable by hand
         — but it must not claim success. */
      patchLocalOperation(origin, {
        copied: false,
        copyError:
          "The password could not be copied. Select and copy it manually.",
      });
    }
  };

  /* Read back for the JSX below, so the dialog markup keeps naming plain values
     rather than reaching into the fenced container at every use. */
  const localAccountOpen = activeLocal !== null;
  const createdLocal = activeLocal?.created ?? null;
  const localDisplayName = activeLocal?.displayName ?? "";
  const localEmail = activeLocal?.email ?? "";
  const localAccess = activeLocal?.access ?? TENANT_MEMBER_ROLE;
  const localLead = activeLocal?.lead ?? false;
  const passwordCopied = activeLocal?.copied ?? false;
  const totalMembers = members.data?.totalCount ?? 0;
  const visibleStart =
    totalMembers === 0 || memberRows.length === 0
      ? 0
      : (memberPage - 1) * NUMBERED_MEMBER_PAGE_SIZE + 1;
  const visibleEnd =
    visibleStart === 0
      ? 0
      : Math.min(totalMembers, visibleStart + memberRows.length - 1);

  return (
    <AdminSection
      icon={Users}
      title="Members & roles"
      description="Add members from the directory or as local accounts, change a member's role, and suspend or remove access."
      action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => setLearnMoreOpen(true)}>
            Learn more
          </Button>
          <Button
            variant="primary"
            className="gap-1.5"
            onClick={() => setActiveTab("add")}
          >
            <UserPlus className="size-4" aria-hidden="true" />
            Add people
          </Button>
        </div>
      }
      meta={null}
    >
      {/* Three tabs, not three stacked blocks. This page used to carry the
          password-account policy, the add-people actions AND the member table
          one under another — a little over two screens of scroll — with the
          members/invites tabs nested inside the last of them. Lifting the tabs
          to the top level puts one job on screen at a time, which is how every
          other admin section is laid out.

          Members is the default because `/admin` redirects here on the grounds
          that "where do I manage people?" is the question an admin arrives with
          (PB-51) — so the roster is what they should land on, not a form.

          The password-account policy stays WITH the add actions rather than
          becoming a fourth tab: those switches decide which of those actions is
          available at all (PB-4826), and split apart, a disabled control would
          appear with its reason on another tab.

          Reuses packages/ui Tabs (APG roles, roving tabindex, aria-controls)
          rather than hand-rolling a tablist. Inactive panels unmount, so the
          badge count is its own small query — see usePendingInviteCount. */}
      <Tabs
        aria-label="Members and roles"
        value={activeTab}
        onValueChange={changeTab}
        items={[
          { value: "members", label: "Members" },
          {
            value: "invites",
            label: (
              <>
                Pending invites
                <PendingInviteBadge />
              </>
            ),
          },
          { value: "add", label: "Add people" },
        ]}
      >
        <TabPanel value="members">
          <div
            id="members-role-help"
            className="my-4 flex items-start gap-3 rounded-lg border border-pb-border bg-pb-card-elevated/35 px-4 py-3 text-dense-1 text-pb-text-muted"
          >
            <ShieldCheck
              className="mt-0.5 size-4 shrink-0 text-pb-teal-text"
              aria-hidden="true"
            />
            <p>{TRANSFORMATION_LEAD_HELP}</p>
          </div>
          {/* Server-side filters. A large tenant can hold far more members than
                the loaded pages, so filtering must reach the database rather than
                the rows already on screen. */}
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-pb-border bg-pb-card-elevated/25 p-3">
            <div className="w-full md:min-w-64 md:flex-[2]">
              <Input
                label="Search members"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Name or email"
                prefix={<Search className="size-3.5" aria-hidden="true" />}
              />
            </div>
            <div className="w-full sm:w-44">
              <Select
                label="Status"
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(v) =>
                  setStatusFilter(v as RosterMembershipStatus | "all")
                }
              />
            </div>
            <div className="w-full sm:w-52">
              <Select
                label="Role"
                options={ROLE_FILTER_OPTIONS}
                value={roleFilter}
                onChange={(v) => setRoleFilter(v as TenantRole | "all")}
              />
            </div>
            <p
              role="status"
              className="w-full text-dense-1 text-pb-text-muted @min-[58.5rem]/settings-page:ms-auto @min-[58.5rem]/settings-page:w-auto @min-[58.5rem]/settings-page:pb-2.5"
            >
              {members.isLoading
                ? "Loading members…"
                : `Showing ${visibleStart}–${visibleEnd} of ${totalMembers} members`}
            </p>
          </div>

          {/* The scroll region that frames the table (PB-5178, d3): the table
              keeps a 61.5rem minimum, scrolls inside this region, and the
              Member column stays frozen at the start edge. In a region
              narrower than 42.5rem the table falls back to a card list; the
              first cell opts out of the pin there (see its className) so no
              stray edge is drawn on a card. The region is an unnamed size
              container, and the table's `@min-[42.5rem]:` rules query it on
              purpose (PB-5436). */}
          <TableScroll aria-label="Members">
            {/* The table picks table or card list from its OWN region, not the
              window (PB-5436): the Player AI side rail narrows main without
              changing the window, and `lg:` kept the desktop table in a
              region too narrow for it. 42.5rem is the region's width at
              today's lg window (1024px) minus the 1rem scrollbar allowance.

                Column model (PB-5436, measured on dev): Status is fixed at
                15rem because its pills neither wrap by default nor truncate —
                the widest English one, "Send unconfirmed {date}", is 202px —
                and the extra pills below wrap their text instead (a longer
                browser-language date then takes a second line, never the next
                column). Last active is 9.5rem (at most two lines). Actions is
                sized for the WIDEST row, which is FIVE icons (re-send
                set-password — PB-5294 —, resume removal — ADR-0078 —,
                sessions, suspend/reactivate, remove — PB-2927): 5×28px
                buttons + 4×4px gaps + 32px cell padding = 188px, so `w-48`
                (192px); the `<th>` carries the same width, so both move
                together. Re-measure for a sixth. Member and Access share what
                is left equally; 61.5rem is the smallest table that still gives
                each 12.5rem (Access then wraps its checkbox under the role
                select), and a narrower region scrolls with Member pinned. */}
            {/* eslint-disable-next-line ds/no-hand-built -- kept: this roster is a responsive table that becomes a card list in a narrow region (block rows, per-cell labels); DataTable would restyle the header and cards, a look change no PR 3 row approves. */}
            <table className="block w-full text-body-small @min-[42.5rem]:table @min-[42.5rem]:min-w-[61.5rem] @min-[42.5rem]:table-fixed">
              <colgroup className="hidden @min-[42.5rem]:table-column-group">
                <col />
                <col />
                <col className="w-60" />
                <col className="w-38" />
                <col className="w-48" />
              </colgroup>
              <thead className="sr-only bg-pb-card-elevated/70 @min-[42.5rem]:not-sr-only @min-[42.5rem]:table-header-group">
                <tr className="border-b border-pb-border text-start">
                  <th className={TH}>Member</th>
                  <th className={TH}>Access</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>
                    <AdminTimeZoneLabel
                      label="Last active"
                      formatter={formatter}
                    />
                  </th>
                  <th className={cn(TH, "w-48 text-end")}>Actions</th>
                </tr>
              </thead>
              <tbody className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3 p-3 @min-[42.5rem]:table-row-group @min-[42.5rem]:p-0">
                {memberRows.map((m) => {
                  const displayName =
                    m.displayName ?? m.email ?? "Unnamed member";
                  /* Mirrors the server's self_lockout: an admin may not
                     demote or deactivate themselves. Cosmetic only. */
                  const isSelf = m.userId === claims?.userId;
                  /* The server rejects any role change on a non-active
                     membership (membership_inactive), so don't offer it. */
                  const roleLocked = isSelf || m.status !== "active";
                  const rolePending = roleRow.isPending(m.userId);
                  const roleError = roleRow.errorFor(m.userId);
                  /* ADR-0078: a Transformation Lead removal is recorded for
                       this member and not yet complete. The list says so with
                       a marker computed once per page, and the row answers
                       with a RESUME action of its own — independent of the
                       role checkbox, because past the revoke stage that box is
                       already unchecked and unchecking it was the only way
                       into the dialog. The checkbox is inert meanwhile: the
                       server refuses a grant on top of an in-flight removal
                       (`removal_in_progress`) and never cancels one. */
                  const removalInProgress =
                    m.transformationLeadRemovalInProgress === true;
                  /* PB-5345 review run 8 (finding 3). Server-decided, so it
                     survives the dialog, a reload and a different
                     administrator — which is the whole point of recording the
                     operation. */
                  const signOutPendingHere =
                    m.passwordResetSignOutPending === true;
                  /* Scoped to THIS member, so a retry that did not finish
                     reports on the row it belongs to. Dropped as soon as the
                     server stops marking the row, so a stale failure cannot
                     outlive the thing it described. */
                  const signOutRetryFailedHere =
                    signOutPendingHere &&
                    signOutRetryRowOutcome?.userId === m.userId &&
                    signOutRetryRowOutcome.outcome === "failed";
                  const statusPending = statusRow.isPending(m.userId);
                  const statusError = statusRow.errorFor(m.userId);
                  const resendError = resendRow.errorFor(m.userId);
                  /* One row can hold more than one failed action: the controls
                     are separate buttons and each keeps its own outcome until
                     it is used again.

                     The finish-sign-out button is deliberately absent. Its
                     refusal already has a surface of its own — the critical
                     "Sign-out retry failed" pill below, whose curated title
                     says what to do next — so a second sentence would only
                     repeat it. That action takes the controller for its LOCK,
                     not for its copy. */
                  const actionErrors: {
                    readonly name: string;
                    readonly text: string;
                  }[] = [];
                  if (statusError)
                    actionErrors.push({ name: "status", text: statusError });
                  if (resendError)
                    actionErrors.push({ name: "resend", text: resendError });
                  /* A status this build does not know renders as a neutral
                     pill with its raw value; dereferencing `undefined` here
                     took the whole roster down. */
                  const statusPill = (
                    STATUS_PILL as Partial<Record<string, StatusPillSpec>>
                  )[m.status] ?? { tone: "neutral", label: m.status };
                  const sync = syncPill(m.syncStatus);
                  /* The token row when there is one; otherwise the same
                     positive evidence the action is gated on. Main added a
                     "Password setup pending" pill keyed on "local and never
                     signed in" while this branch was open, which is the right
                     intent on the wrong predicate — it sweeps in accounts an
                     administrator created directly, who hold a temporary
                     password and owe nothing. Merged here as ONE pill rather
                     than two: the outcome-aware wording where a link is known,
                     and an honest "no record" where the person predates the
                     flow. `joinedByInvitation` is what keeps the second case
                     from claiming anything about the first population. */
                  /* `owesPassword` alone, because this branch is only reached
                     when there is no live token row — and in that case the
                     server can only have said yes via its invited-and-never-
                     signed-in arm, so repeating those two here restated the
                     server's own rule in the browser. That restatement is the
                     thing this field was introduced to remove (review of
                     PR #275). */
                  const awaitingPassword =
                    setPasswordPill(m.setPassword) ??
                    (m.status === "active" &&
                    m.authenticationMethod === "local" &&
                    m.owesPassword === true
                      ? SET_PASSWORD_NO_RECORD
                      : null);
                  /* The pill and the re-send action answer different
                     questions, so they are gated differently.

                     The pill reports a link this page knows about; no row
                     means it knows nothing, and an empty state is not a
                     verdict, so there is no pill.

                     The action is offered wider, because the population this
                     feature exists for has no row at all: everyone invited
                     before PB-5294 was created in Keycloak with
                     UPDATE_PASSWORD and no credential, so `awaitsPassword`
                     holds and the server WILL mint them a link. Gating the
                     button on the token table would hide the flow from
                     exactly the people who still need it.

                     What the row can settle without asking Keycloak is that a
                     link is PLAUSIBLE — an active local account that JOINED BY
                     INVITATION and has never signed in.

                     That last clause is not decoration. An account an
                     administrator created directly looks identical here —
                     `source` is "admin" for both routes, both are local, both
                     have never signed in — but it was created holding a
                     temporary password, so the server refuses its re-send
                     EVERY time and tells the administrator the person "has
                     already set a password", which is not what happened. An
                     action that is always refused, under a reason that is
                     untrue, is worse than no action. `joinedByInvitation` is
                     the one fact that separates them, and the server computes
                     it in the same batch read as the rest of this row.

                     Past that, the server stays the authority: an invitee who
                     has in fact already set a password is still refused, in
                     words written for the administrator. Settling THAT here
                     would cost a Keycloak round trip per member, per page. */
                  /* PB-5345, correcting PB-5294's heuristic (CodeRabbit,
                     PR #275). `owesPassword` is decided by the server, which is
                     the only place that can: completing a set-password link
                     retires the token, so `setPassword` goes null while
                     `joinedByInvitation` stays historical and `firstSignedInAt`
                     stays null until the member signs in. The old expression
                     read that window as "still owes one" and offered a re-send
                     the server refuses with `already_has_password`. */
                  const canResendSetPassword =
                    m.status === "active" &&
                    m.authenticationMethod === "local" &&
                    m.owesPassword === true;
                  /* The inverse, on the SAME server-decided answer rather
                     than on the other gate's expression. A local member either
                     owes a password or holds one, and deriving both from one
                     field is what keeps them from drifting into offering the
                     button that cannot work while hiding the one that can. */
                  /* NOT ON YOUR OWN ROW. The server refuses a self-reset
                     (`cannot_reset_own_password`, AC-6 — a reset must sign the
                     subject out everywhere, and sparing the acting session
                     spared every other one too), so offering the button here
                     would only produce a 409. The server is still the gate;
                     this keeps the console from inviting the refusal. */
                  const canResetPassword =
                    !isSelf &&
                    m.status === "active" &&
                    m.authenticationMethod === "local" &&
                    m.owesPassword === false;
                  return (
                    <tr
                      key={m.userId}
                      /* Rows arrive rather than appearing (ADR-0091). No
                         stagger: a page holds up to 50 members, which is a
                         list to read, not a sequence to watch. The roster is
                         paginated, never virtualized, so nothing re-mounts on
                         scroll to replay the entrance. */
                      className="pb-enter-row block min-w-0 max-w-full rounded-xl border border-pb-border bg-pb-card-elevated/30 p-4 transition-colors hover:pb-overlay-paint @min-[42.5rem]:table-row @min-[42.5rem]:rounded-none @min-[42.5rem]:border-x-0 @min-[42.5rem]:border-t-0 @min-[42.5rem]:bg-transparent @min-[42.5rem]:p-0 @min-[42.5rem]:last:border-b-0"
                    >
                      {/* Card mode (a narrow region) opts out of the region's pin with
                          important overrides: the pin is a descendant selector
                          on the region and outranks a plain utility here. */}
                      <td className="block min-w-0 @min-[42.5rem]:table-cell @min-[42.5rem]:px-4 @min-[42.5rem]:py-3 @max-[42.5rem]:static! @max-[42.5rem]:border-e-0! @max-[42.5rem]:bg-transparent!">
                        <div
                          className={cn(
                            PIN_FIRST_COLUMN_CONTENT,
                            // The cap is for a pinned cell; a card has no pin
                            // and the name would otherwise truncate at half
                            // the card ("T…" beside the You pill at 375px).
                            "@max-[42.5rem]:max-w-none",
                            "flex min-w-0 items-center gap-3",
                          )}
                        >
                          {/* The product's per-person tone (PB-5134 d36),
                              seeded by the user id so a member keeps one
                              colour across the console, the top bar and
                              every chip — the artboard's `.uav` gradient. */}
                          <Avatar size="sm" seed={m.userId}>
                            <AvatarFallback>
                              {initials(displayName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-pb-text font-medium truncate">
                                {displayName}
                              </span>
                              {isSelf && (
                                <StatusPill tone="info">You</StatusPill>
                              )}
                            </div>
                            <div className="text-xs text-pb-text-muted truncate">
                              {m.email ?? "No email"}
                            </div>
                            <div className="mt-1 text-dense-1 text-pb-text-muted">
                              {AUTH_METHOD_LABELS[m.authenticationMethod]}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="mt-4 block border-t border-pb-border pt-3 @min-[42.5rem]:table-cell @min-[42.5rem]:border-t-0 @min-[42.5rem]:px-4 @min-[42.5rem]:py-3">
                        <span className={CARD_LABEL}>Access</span>
                        {/* Wraps rather than spilling: in a narrow Access
                            column the checkbox moves under the role select
                            instead of running into Status (PB-5436). */}
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                          {/* Base access. Changing it KEEPS the lead half:
                                the complete set travels every time, so a
                                promotion to administrator can never silently
                                drop Transformation Lead, or the reverse.

                                Picking CONFIRMS first (PB-5406) rather than
                                writing. Nothing local changes meanwhile — the
                                trigger keeps rendering the stored role — so
                                cancelling restores nothing and needs no undo. */}
                          <Select
                            aria-label={`Role for ${displayName}`}
                            className="max-w-full sm:w-48"
                            options={ROLE_OPTIONS}
                            value={baseAccessOf(m)}
                            disabled={roleLocked || rolePending}
                            onChange={(v) => {
                              const next = asBaseAccess(v);
                              /* Select commits whatever is clicked, including
                                 the option already selected. Without this the
                                 row asked to confirm a change to the role the
                                 member already holds — and before PB-5406 it
                                 sent that no-op write. */
                              if (next === baseAccessOf(m)) return;
                              setRoleConfirmation({
                                scope,
                                request: {
                                  kind: "base-access",
                                  member: m,
                                  next,
                                },
                              });
                            }}
                          />
                          {/* Both directions confirm, and they confirm
                                DIFFERENT things. Granting asks a plain yes or
                                no (PB-5406). REMOVING opens the transfer
                                dialog, because the server refuses to drop the
                                role while the member owns LAMA resources — and
                                that dialog is where the administrator picks
                                who takes them over. */}
                          <Checkbox
                            aria-label={`Transformation Lead for ${displayName}`}
                            aria-describedby="members-role-help"
                            label="Transformation Lead"
                            checked={isTransformationLead(m)}
                            disabled={
                              roleLocked || rolePending || removalInProgress
                            }
                            onChange={(checked) => {
                              if (checked) {
                                setRoleConfirmation({
                                  scope,
                                  request: { kind: "lead-grant", member: m },
                                });
                              } else {
                                setLeadRemoval({ tenantId, member: m });
                              }
                            }}
                          />
                        </div>
                        {/* Outside the controls' flex row, or it becomes a
                            sibling COLUMN of the select at `sm` and is squeezed
                            into a few characters.

                            ONE LINE, bounded and truncated, with the whole
                            sentence on `title` — the treatment apps/shell's
                            console-table rule names for a failure reason. A cell
                            never wraps a sentence: one long refusal would
                            otherwise set the row height for every row beside it.
                            The inline Alert's content slot is already
                            `min-w-0 flex-1`, so the span shrinks and clips
                            inside it. */}
                        {roleError ? (
                          <Alert
                            variant="inline"
                            tone="critical"
                            className="mt-1.5 max-w-56"
                          >
                            <span className="block truncate" title={roleError}>
                              {roleError}
                            </span>
                          </Alert>
                        ) : null}
                      </td>
                      <td className="mt-3 block @min-[42.5rem]:table-cell @min-[42.5rem]:px-4 @min-[42.5rem]:py-3 @min-[42.5rem]:whitespace-nowrap">
                        <span className={CARD_LABEL}>Status</span>
                        <div className="flex flex-col items-start gap-1.5">
                          <StatusPill tone={statusPill.tone}>
                            {statusPill.label}
                          </StatusPill>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {/* Additive, never a replacement for the pill beside
                              it: "was told" and "has signed in" are different
                              facts, and reading them together is the point. */}
                            {(() => {
                              const pill = notifiedPill(
                                notifiedByUserId.get(m.userId),
                                date,
                              );
                              return pill ? (
                                <StatusPill
                                  tone={pill.tone}
                                  className={EXTRA_PILL}
                                >
                                  {pill.label}
                                </StatusPill>
                              ) : null;
                            })()}
                            {/* The identity-sync exception, if there is one:
                              "is a member" and "can sign in yet" are also
                              different facts (PB-5216, d3). */}
                            {sync ? (
                              <StatusPill
                                tone={sync.tone}
                                title={sync.title}
                                className={EXTRA_PILL}
                              >
                                {sync.label}
                              </StatusPill>
                            ) : null}
                            {awaitingPassword ? (
                              <StatusPill
                                tone={awaitingPassword.tone}
                                title={awaitingPassword.title}
                                className={EXTRA_PILL}
                              >
                                {awaitingPassword.label}
                              </StatusPill>
                            ) : null}
                            {/* PB-5345 review run 8. A reset whose credential
                              landed but whose sign-out did not finish. The
                              password IS changed — saying otherwise would send
                              an administrator to redo something that worked —
                              but the member may still be signed in somewhere,
                              which is the half of AC-6 that did not happen.
                              The row's own action finishes it. */}
                            {signOutPendingHere ? (
                              <StatusPill
                                tone="watch"
                                className={EXTRA_PILL}
                                title="Their password was changed, but signing them out of existing sessions did not finish. Use the sign-out action on this row to complete it."
                              >
                                Sign-out pending
                              </StatusPill>
                            ) : null}
                            {signOutRetryFailedHere ? (
                              <StatusPill
                                tone="critical"
                                className={EXTRA_PILL}
                                title="Signing this member out still did not complete. Try again, or revoke their sessions directly."
                              >
                                Sign-out retry failed
                              </StatusPill>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="mt-3 block @min-[42.5rem]:table-cell @min-[42.5rem]:px-3 @min-[42.5rem]:py-3">
                        <span className={CARD_LABEL}>
                          <AdminTimeZoneLabel
                            label="Last active"
                            formatter={formatter}
                            variant="inline"
                          />
                        </span>
                        <span className="text-pb-text-muted">
                          {m.lastSignedInAt
                            ? dateTime(m.lastSignedInAt)
                            : m.firstSignedInAt
                              ? "Unavailable"
                              : "Never signed in"}
                        </span>
                      </td>
                      <td className="mt-3 block border-t border-pb-border pt-3 text-end @min-[42.5rem]:table-cell @min-[42.5rem]:border-t-0 @min-[42.5rem]:px-4 @min-[42.5rem]:py-3">
                        {/* Suspending or removing yourself can never
                            succeed, so the row simply has no actions —
                            a permanently-disabled icon reads as a
                            rendering fault, not as a guard. The
                            align-[6px] lift (layout only) puts the box bottom
                            back on the text baseline, where the old
                            hand-built icons sat; the DS Button's baseline is
                            its glyph, 6px higher, which dropped the row 2px. */}
                        <div className="inline-flex items-center gap-1 align-[6px]">
                          {/* Offered whatever the checkbox shows, and to any
                                administrator: the operation is resumable
                                without the client that started it, and the
                                dialog adopts it through the recovery read
                                rather than minting a second key. */}
                          {removalInProgress ? (
                            <Tooltip content="Resume Transformation Lead removal">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Resume removal for ${displayName}`}
                                onClick={() =>
                                  setLeadRemoval({ tenantId, member: m })
                                }
                              >
                                <StepForward className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : null}
                          {/* Wider than the pill beside it, and on
                              purpose — see `canResendSetPassword`. */}
                          {/* PB-5345. Exactly one of these two, never both:
                              a member who still owes a password is re-sent a
                              link, and everybody else can be reset. NOT on your
                              own row — review run 4 decided the ticket's open
                              question against self-reset, because skipping the
                              sign-out to keep the acting session alive kept
                              every other session alive too. */}
                          {canResetPassword ? (
                            <Tooltip content="Reset password">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Reset the password for ${displayName}`}
                                /* Fenced on the session as well as the member
                                   (CodeRabbit, PR #275): a request the previous
                                   administrator left in flight would otherwise
                                   keep the next one from opening a reset for that
                                   member until it settled. Same fence the dialog
                                   already applies, one level out. */
                                disabled={resetPasswordRow.isPending(m.userId)}
                                onClick={() => {
                                  nextPasswordResetId.current += 1;
                                  setPasswordResetCopiedFor(null);
                                  setPasswordResetCopyError(null);
                                  setPasswordReset({
                                    operationId: nextPasswordResetId.current,
                                    scope,
                                    member: m,
                                    issued: null,
                                  });
                                }}
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : null}
                          {m.passwordResetSignOutPending === true ? (
                            <Tooltip content="Finish signing this member out">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Finish signing ${displayName} out after their password reset`}
                                disabled={finishSignOutRow.isPending(m.userId)}
                                onClick={() =>
                                  finishSignOutRow.run({
                                    userId: m.userId,
                                    scope,
                                  })
                                }
                              >
                                <LogOut className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : null}
                          {canResendSetPassword ? (
                            <Tooltip content="Re-send the set-password email">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Re-send the set-password email to ${displayName}`}
                                disabled={resendRow.isPending(m.userId)}
                                onClick={() => {
                                  /* A counter ref, not a random or time value:
                                     deterministic in tests, and monotonic per
                                     mount is all the uniqueness this needs —
                                     ids are only ever compared with ids from
                                     the same component instance, exactly as
                                     `nextLocalDialogId` documents. */
                                  nextResendId.current += 1;
                                  resendRow.run({
                                    operationId: nextResendId.current,
                                    userId: m.userId,
                                    member: m,
                                    scope,
                                  });
                                }}
                              >
                                <MailPlus className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : null}
                          {isSelf ? null : m.status === "active" ? (
                            <Tooltip content="Suspend member">
                              <Button
                                variant="ghost"
                                tone="critical"
                                size="sm"
                                iconOnly
                                aria-label={`Suspend ${displayName}`}
                                disabled={statusPending}
                                onClick={() => setSuspendTarget(m)}
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : (
                            <Tooltip content="Reactivate member">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Reactivate ${displayName}`}
                                disabled={statusPending}
                                onClick={() =>
                                  statusRow.run({
                                    userId: m.userId,
                                    status: "active",
                                    member: m,
                                    scope,
                                  })
                                }
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          )}
                          {/* Available for a suspended member too: they can
                                  still hold live sessions, which is exactly when
                                  an admin wants to end them. */}
                          {m.status !== "removed" ? (
                            <Tooltip content="Sign-in sessions">
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Sign-in sessions for ${displayName}`}
                                onClick={() =>
                                  setSessionsState({ tenantId, member: m })
                                }
                              >
                                <MonitorSmartphone className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : null}
                          {!isSelf && m.status !== "removed" ? (
                            <Tooltip content="Remove member">
                              <Button
                                variant="ghost"
                                tone="critical"
                                size="sm"
                                iconOnly
                                aria-label={`Remove ${displayName}`}
                                disabled={statusPending}
                                onClick={() => setRemoveTarget(m)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          ) : null}
                        </div>
                        {actionErrors.map((failure) => (
                          <Alert
                            key={failure.name}
                            variant="inline"
                            tone="critical"
                            className="mt-1 ms-auto max-w-56 text-start"
                          >
                            <span
                              className="block truncate"
                              title={failure.text}
                            >
                              {failure.text}
                            </span>
                          </Alert>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* The three settled states the table itself can't express. Kept
                distinct so a slow first page never renders as "no members". */}
            {members.isLoading ? (
              <p
                role="status"
                className="sticky start-0 border-t border-pb-border px-4 py-6 text-center text-sm text-pb-text-muted"
              >
                Loading members...
              </p>
            ) : null}
            {members.error ? (
              /* Was the server's sentence alone, centred in red, with nothing
                 saying WHICH part of the page was missing and no way back. */
              <div className="sticky start-0 border-t border-pb-border">
                <AsyncSection
                  status="error"
                  errorTitle="Couldn't load the members"
                  errorDescription={members.error.message}
                  onRetry={() => {
                    members.refetch();
                  }}
                >
                  {null}
                </AsyncSection>
              </div>
            ) : null}
            {/* Said out loud, because silence would LIE. A missing pill
                    normally means "never notified", so in the two states where
                    the page does not know, absence would assert that of
                    everybody.

                    Which is why the second sentence has to NEGATE the usual
                    reading rather than restate it. It said "Nothing here means it
                    was not sent" — the very claim the guard exists to prevent,
                    printed in the only states where it cannot be true. The guard
                    was right and its own explanation contradicted it.

                    `role="status"` rather than `alert`: the member list itself is
                    fine, one column of it is not. */}
            {notifiedStatusUnknown ? (
              <p
                role="status"
                className="sticky start-0 border-t border-pb-border px-4 py-3 text-center text-sm text-pb-text-muted"
              >
                {notified.isError
                  ? "Notification status could not be loaded, so this list is not showing who was emailed. A missing indicator does not mean an email was not sent."
                  : "Checking who was emailed. Until this finishes, a missing indicator does not mean an email was not sent."}
              </p>
            ) : null}
            {!members.isLoading &&
            !members.error &&
            memberRows.length === 0 &&
            totalMembers === 0 ? (
              <p
                role="status"
                className="sticky start-0 border-t border-pb-border px-4 py-6 text-center text-sm text-pb-text-muted"
              >
                {debouncedMemberSearch ||
                statusFilter !== "all" ||
                roleFilter !== "all"
                  ? "No members match these filters."
                  : "No members yet."}
              </p>
            ) : null}
            {members.data && members.data.pageCount > 1 ? (
              <div className="sticky start-0 flex justify-center border-t border-pb-border p-3">
                <Pagination
                  mode="numbered"
                  page={memberPage}
                  pageCount={members.data.pageCount}
                  onChange={setMemberPage}
                />
              </div>
            ) : null}
          </TableScroll>
        </TabPanel>

        <TabPanel value="invites">
          <InvitesTabPanel />
        </TabPanel>

        <TabPanel value="add">
          <SettingsGroup
            title="Add people"
            description="Provision members from Microsoft Entra, invite an address that isn't in the directory yet, or create a local account for an external user or test account."
          >
            <Card className="mb-4 overflow-hidden">
              <CardBody flush>
                <div className="flex items-start gap-3 border-b border-pb-border bg-pb-card-elevated/35 px-4 py-3.5">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-pb-teal-soft text-pb-teal-text">
                    <Building2 className="size-4.5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-pb-text">
                      Add from Microsoft Entra
                    </h3>
                    <p className="mt-0.5 text-xs leading-relaxed text-pb-text-muted">
                      Use the company directory first. Access stays connected to
                      the person's managed identity.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_160px_auto] gap-3 items-end">
                    <FormField label="Directory member">
                      {/* Keyed on the tenant, so a handover remounts it and
                          drops the typed term: a person's name or address,
                          which the incoming administrator must neither see nor
                          search under their own tenant. */}
                      <PeopleField
                        key={tenantId ?? "none"}
                        label="Directory member"
                        emptyLabel="Find a person"
                        source={directory.source}
                        labels={DIRECTORY_FIELD_LABELS}
                        value={
                          selectedDirectoryUser
                            ? {
                                kind: "person",
                                id: selectedDirectoryUser.directoryUserId,
                                name: selectedDirectoryUser.displayName,
                              }
                            : null
                        }
                        onChange={(next) =>
                          setSelectedDirectoryUser(
                            next?.kind === "person"
                              ? (directory.users.get(next.id) ?? null)
                              : null,
                          )
                        }
                      />
                    </FormField>
                    <Select
                      label="Role"
                      options={ROLE_OPTIONS}
                      value={provisionAccess}
                      onChange={(v) => setProvisionAccess(asBaseAccess(v))}
                    />
                    <Button
                      variant="primary"
                      className="gap-1.5"
                      disabled={
                        !selectedDirectoryUser ||
                        !!selectedDirectoryUser.membership ||
                        provisioning
                      }
                      onClick={() => {
                        if (!selectedDirectoryUser) return;
                        provision.mutate({
                          scope,
                          directoryUserId:
                            selectedDirectoryUser.directoryUserId,
                          roles: composeTenantRoles(
                            provisionAccess,
                            provisionLead,
                          ),
                        });
                      }}
                    >
                      <UserPlus className="w-3.5 h-3.5" /> Add member
                    </Button>
                  </div>
                  {/* Members stay in the results, so a pick says why Add
                      member stays off instead of the person seeming missing. */}
                  {selectedDirectoryUser?.membership ? (
                    <p role="status" className="text-xs text-pb-text-muted">
                      {selectedDirectoryUser.displayName} is already a member of
                      this workspace.
                    </p>
                  ) : null}
                  <Checkbox
                    label="Also grant Transformation Lead"
                    checked={provisionLead}
                    onChange={setProvisionLead}
                  />
                  <p className="text-xs leading-relaxed text-pb-text-muted">
                    {TRANSFORMATION_LEAD_HELP}
                  </p>
                  {/* role="alert", like the member-action and local-account failures
                below: focus stays on "Add member" when the request fails, so
                without it the message appears silently to a screen reader. */}
                  {provisionError ? (
                    <p role="alert" className="text-sm text-pb-red-text">
                      {provisionError.message}
                    </p>
                  ) : null}
                </div>
              </CardBody>
            </Card>

            <div className="mb-5">
              <div className="mb-2.5">
                <h3 className="font-semibold text-pb-text">
                  Other ways to add someone
                </h3>
                <p className="mt-0.5 text-xs text-pb-text-muted">
                  Use these when the person is not available in the company
                  directory.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex items-start gap-3 rounded-xl border border-pb-border bg-pb-card-elevated/25 p-4">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-pb-accent/10 text-pb-accent-text">
                    <InviteIcon className="size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-pb-text">
                      Email invitation
                    </h4>
                    <p className="mt-1 min-h-10 text-xs leading-relaxed text-pb-text-muted">
                      Let the person verify their email and choose their own
                      password.
                    </p>
                    <Button
                      variant="secondary"
                      className="mt-3 gap-1.5"
                      onClick={() => openInvite()}
                      disabled={!localInvitesAllowed}
                    >
                      <InviteIcon className="size-3.5" aria-hidden="true" />
                      Invite by email
                    </Button>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-pb-border bg-pb-card-elevated/25 p-4">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-pb-card-elevated text-pb-text-secondary">
                    <KeyRound className="size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-pb-text">Local account</h4>
                    <p className="mt-1 min-h-10 text-xs leading-relaxed text-pb-text-muted">
                      Create a temporary password for an external collaborator
                      or test account.
                    </p>
                    <Button
                      variant="secondary"
                      className="mt-3 gap-1.5"
                      onClick={openLocalAccount}
                      disabled={!localAccountsAllowed}
                    >
                      <KeyRound className="size-3.5" aria-hidden="true" />
                      Create local account
                    </Button>
                  </div>
                </div>
              </div>
              {localAccountsAllowed ? null : (
                <p className="mt-3 text-xs leading-relaxed text-pb-text-muted">
                  {accessPolicyPending
                    ? "Checking whether password accounts are allowed in this workspace..."
                    : accessPolicyFailed
                      ? "We couldn't check whether password accounts are allowed, so creating one is unavailable. Use Try again under Password accounts below."
                      : "Password accounts are turned off for this workspace, so neither an email invitation nor a local account can be created. Turn them on under Password accounts below."}
                </p>
              )}
              {localAccountsAllowed && !localInvitesAllowed ? (
                <p className="mt-3 text-xs leading-relaxed text-pb-text-muted">
                  New password-account invitations are turned off for this
                  workspace, so an email invitation cannot be created. Existing
                  password accounts still work. Turn new invitations on under
                  Password accounts below.
                </p>
              ) : null}
            </div>
          </SettingsGroup>
          {/* Policy follows the primary add-person task. The compact status copy
              above explains disabled actions immediately; this panel is where
              an administrator changes the governing controls. */}
          <AccessPolicyPanel />
        </TabPanel>
      </Tabs>

      {inviteDialog}

      <Modal
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
        title="Managing members and roles"
        description="A quick guide to access, onboarding and offboarding in this workspace."
        describedBy="members-help-overview"
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setLearnMoreOpen(false)}>
            Done
          </Button>
        }
      >
        <div
          id="members-help-overview"
          className="space-y-4 text-sm text-pb-text-secondary"
        >
          <section>
            <h3 className="font-semibold text-pb-text">Roles</h3>
            <p className="mt-1">
              Members use the workspace, admins manage it, and Transformation
              Leads manage LAMA questionnaires, campaigns and reports. Admin and
              Transformation Lead are independent.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">Add people</h3>
            <p className="mt-1">
              Add company users from Microsoft Entra. Use an invitation when
              someone should choose their own password, or create a local
              account when an admin must hand over a temporary password.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">Invitations</h3>
            <p className="mt-1">
              An invitation remains pending until redeemed, revoked or expired.
              The invitee confirms their email and completes password setup in
              the identity provider. Revoke and recreate an invite to change its
              intended roles.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">Access and sessions</h3>
            <p className="mt-1">
              Suspending keeps the member visible and lets an admin reactivate
              them. Removing hides the soft-deleted membership from this roster.
              Ending sessions signs the member out without changing their
              membership.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">
              Transformation Lead removal
            </h3>
            <p className="mt-1">
              LAMA ownership must be transferred before this role is removed. An
              interrupted transfer remains resumable from the member row.
            </p>
          </section>
        </div>
      </Modal>

      <Modal
        open={localAccountOpen}
        onClose={closeLocalAccount}
        title={createdLocal ? "Local account created" : "Create local account"}
        description={
          createdLocal
            ? "Copy the temporary password before closing this dialog."
            : "For external users and test accounts that do not sign in through Microsoft Entra."
        }
        size="sm"
        footer={
          createdLocal ? (
            /* Secondary, matching the SCIM secret panel: the accent belongs on
               Copy, which is what must still be done, not on the dismissal. */
            <Button variant="secondary" onClick={closeLocalAccount}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                disabled={localPending}
                onClick={closeLocalAccount}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={
                  localPending ||
                  !localDisplayName.trim() ||
                  !localEmail.trim() ||
                  !localEmail.includes("@")
                }
                onClick={submitLocalAccount}
              >
                {localPending ? "Creating account..." : "Create account"}
              </Button>
            </>
          )
        }
      >
        {createdLocal ? (
          <div className="flex flex-col gap-4">
            {/* Look row OK-25: the label/value pairs are the DescriptionList. */}
            <DescriptionList>
              <DescriptionItem label="Email">
                {createdLocal.member.email}
              </DescriptionItem>
              <DescriptionItem label="Temporary password">
                <div className="flex items-center gap-2 rounded-md border border-pb-border bg-pb-card-elevated p-2.5">
                  <code
                    data-testid="temporary-password"
                    className="min-w-0 flex-1 break-all text-sm text-pb-text"
                  >
                    {createdLocal.temporaryPassword}
                  </code>
                  <Button
                    variant="primary"
                    className="shrink-0 gap-1.5"
                    onClick={() => void copyTemporaryPassword()}
                  >
                    {passwordCopied ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                    {passwordCopied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </DescriptionItem>
            </DescriptionList>
            <p className="text-sm text-pb-text-secondary">
              This password cannot be viewed again. The user must replace it and
              their profile name will refresh from the identity provider when
              they sign in.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Inert while the request is in flight, like OrganizationAdmin's
                fields during a save — and here it is a correctness guard, not
                just a spinner courtesy. `createLocal.reset()` below clears the
                variables `localPending` is derived from WITHOUT cancelling the
                request, so an editable field would retire the pending state and
                reopen both the duplicate-submit guard on Create account and the
                dismissal gate in `closeLocalAccount`. Dismissing then drops a
                temporary password the account has already been given. */}
            <Input
              label="Display name"
              autoComplete="name"
              value={localDisplayName}
              disabled={localPending}
              onChange={(event) => {
                patchLocal({ displayName: event.target.value });
                if (!localPending) createLocal.reset();
              }}
            />
            <Input
              label="Email"
              type="email"
              autoComplete="off"
              value={localEmail}
              disabled={localPending}
              onChange={(event) => {
                patchLocal({ email: event.target.value });
                /* Editing retires the previous attempt's failure, the way
                   clearing `localError` used to. Guarded as well as gated: the
                   `disabled` above is what makes this unreachable mid-flight,
                   and this states the invariant at the line that would do the
                   damage, so re-enabling the field cannot quietly resurrect the
                   bug. */
                if (!localPending) createLocal.reset();
              }}
            />
            <Select
              label="Role"
              options={ROLE_OPTIONS}
              value={localAccess}
              disabled={localPending}
              onChange={(value) => patchLocal({ access: asBaseAccess(value) })}
            />
            <Checkbox
              label="Also grant Transformation Lead"
              checked={localLead}
              disabled={localPending}
              onChange={(checked) => patchLocal({ lead: checked })}
              aria-describedby="local-account-transformation-lead-help"
            />
            <p
              id="local-account-transformation-lead-help"
              className="text-xs leading-relaxed text-pb-text-muted"
            >
              {TRANSFORMATION_LEAD_HELP}
            </p>
          </div>
        )}
        {localErrorText ? (
          <p className="mt-3 text-sm text-pb-red-text" role="alert">
            {localErrorText}
          </p>
        ) : null}
      </Modal>

      {/* One dialog for both role paths (PB-5406). Two would duplicate the
          tenant fence above, and the fence is the part that matters. */}
      {(() => {
        if (!roleConfirmTarget) return null;
        const copy = roleConfirmationCopy(
          roleConfirmTarget,
          memberLabel(roleConfirmTarget.member),
        );
        /* ConfirmDialog (d2): removing a role is the destructive tone, granting
           one the neutral. */
        return (
          <ConfirmDialog
            open
            onClose={() => setRoleConfirmation(null)}
            title={copy.title}
            tone={
              copy.confirmVariant === "destructive" ? "critical" : "neutral"
            }
            confirmLabel={copy.confirmLabel}
            onConfirm={() => {
              /* Compose from the LIVE row while the roster still holds
                       it, falling back to the snapshot the dialog opened with.
                       Every write carries the COMPLETE set, so composing from a
                       snapshot would silently strip a role another
                       administrator granted while this dialog sat open. */
              const member =
                memberRows.find(
                  (row) => row.userId === roleConfirmTarget.member.userId,
                ) ?? roleConfirmTarget.member;
              roleRow.run({
                userId: member.userId,
                roles:
                  roleConfirmTarget.kind === "base-access"
                    ? composeTenantRoles(
                        roleConfirmTarget.next,
                        isTransformationLead(member),
                      )
                    : composeTenantRoles(baseAccessOf(member), true),
                member,
                change: memberChangeOfRoles(roleConfirmTarget),
                scope,
              });
              setRoleConfirmation(null);
            }}
          >
            <p>{copy.description}</p>
            <p className="mt-2">{copy.body}</p>
          </ConfirmDialog>
        );
      })()}

      {/* ConfirmDialog (d2): the destructive confirm, red chip, quiet Cancel. */}
      <ConfirmDialog
        open={suspendTarget !== null}
        onClose={() => setSuspendTarget(null)}
        title={`Suspend ${memberLabel(suspendTarget ?? undefined)}?`}
        confirmLabel="Suspend member"
        onConfirm={() => {
          if (!suspendTarget) return;
          statusRow.run({
            userId: suspendTarget.userId,
            status: "suspended",
            member: suspendTarget,
            scope,
          });
          setSuspendTarget(null);
        }}
      >
        <p>
          They will lose access to this workspace until an admin reactivates
          them.
        </p>
        <p className="mt-2">
          Their work, comments and audit history stay intact. You can reactivate
          their access later.
        </p>
      </ConfirmDialog>

      {/* Removal confirms first (PB-14). The server still re-checks last_admin
          and self_lockout. */}
      {/* Confirming hands off to the same fire-and-forget path the suspend and
          reactivate icons use: the dialog closes, the row's own in-flight lock
          takes over, and a refusal surfaces in the table alert. No pending
          state is rendered here because the dialog is already closed by then. */}
      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={`Remove ${memberLabel(removeTarget ?? undefined)}?`}
        confirmLabel="Remove member"
        onConfirm={() => {
          if (!removeTarget) return;
          statusRow.run({
            userId: removeTarget.userId,
            status: "removed",
            member: removeTarget,
            scope,
          });
          setRemoveTarget(null);
        }}
      >
        <p>They will lose access and no longer appear in the Members list.</p>
        <p className="mt-2">
          Their Solves, comments and audit history stay intact as an offboarding
          record.
        </p>
      </ConfirmDialog>

      <TransformationLeadRemovalDialog
        tenantId={tenantId}
        currentUserId={claims?.userId ?? null}
        member={
          leadRemovalTarget
            ? {
                userId: leadRemovalTarget.userId,
                label: memberLabel(leadRemovalTarget),
              }
            : null
        }
        onClose={() => setLeadRemoval(null)}
        onConfirm={async (input) => {
          if (!leadRemovalTarget) return;
          await removeLead.mutateAsync({
            userId: leadRemovalTarget.userId,
            input,
            member: leadRemovalTarget,
            scope,
          });
          setLeadRemoval(null);
        }}
      />

      <SessionsModal
        tenantId={tenantId}
        isSelf={sessionsMember?.userId === claims?.userId}
        member={
          sessionsMember
            ? {
                userId: sessionsMember.userId,
                label: memberLabel(sessionsMember),
              }
            : null
        }
        onClose={() => setSessionsState(null)}
      />
      {/* PB-5294 (AC-7). The link, shown ONCE.

          This is what makes the re-send useful in a workspace whose mail is
          not configured — which, until this ticket, was every workspace. The
          invitation dialog reached the same conclusion and shows `inviteUrl`
          the same way; PB-4922 removed that fallback and had to put it back.

          The link is held in component state only and never persisted:
          `localStorage`/`sessionStorage` are forbidden for security-relevant
          state, and this is a live credential until it is used. */}
      <Modal
        open={activeSetPasswordLink !== null}
        title="Set-password link"
        onClose={() => setSetPasswordLink(null)}
        footer={
          <Button variant="secondary" onClick={() => setSetPasswordLink(null)}>
            Done
          </Button>
        }
      >
        {activeSetPasswordLink ? (
          <div className="flex flex-col gap-4">
            <DescriptionList>
              <DescriptionItem label="Member">
                {memberLabel(activeSetPasswordLink.member)}
              </DescriptionItem>
              <DescriptionItem label="Set-password link">
                <div className="flex items-center gap-2 rounded-md border border-pb-border bg-pb-card-elevated p-2.5">
                  <code
                    data-testid="set-password-link"
                    className="min-w-0 flex-1 break-all text-sm text-pb-text"
                  >
                    {activeSetPasswordLink.url}
                  </code>
                  <Button
                    variant="primary"
                    className="shrink-0 gap-1.5"
                    onClick={() => void copySetPasswordLink()}
                  >
                    {setPasswordLinkCopied ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                    {setPasswordLinkCopied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </DescriptionItem>
            </DescriptionList>
            {/* The expiry, in the reader's own zone, because an administrator
                who is about to pass this link on by hand is the one person who
                has to know when it stops working — the server returns it for
                exactly that reason. Same sentence and same place as the
                invitation dialog's, so the two credential modals read alike. */}
            {/* Three branches, for the reason the invitation dialog gives
                (PB-5435): "queued" only once an active server is confirmed and
                sending is on, and while that is unknown the paused guidance
                without the pause claim — never the "queued" sentence. */}
            {sendingState === "paused" ? (
              <Alert tone="watch" title="Workspace email is paused">
                If they have not received the set-password email, send this link
                yourself.
              </Alert>
            ) : sendingState === "on" ? (
              <p className="text-sm text-pb-text-secondary">
                We have queued an email with this to them. If this workspace has
                no mail server set up, that email cannot be delivered — send
                this link on yourself instead.
              </p>
            ) : (
              <p className="text-sm text-pb-text-secondary">
                If they have not received the set-password email, send this link
                yourself.
              </p>
            )}
            {/* The two facts that hold whatever the mail did, kept in ONE
                paragraph so the sentence and its expiry stay together. */}
            <p className="text-sm text-pb-text-secondary">
              It works once and cannot be shown again. The link stays valid
              until{" "}
              <AdminTimeZoneLabel
                label={dateTimeOr(activeSetPasswordLink.expiresAt, "—")}
                formatter={formatter}
                variant="inline"
              />
              .
            </p>
            {setPasswordLinkCopyErrorText ? (
              <p className="text-sm text-pb-red-text" role="alert">
                {setPasswordLinkCopyErrorText}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>

      {/* PB-5345. Two steps in one dialog: confirm, then the password.

          The confirm is not ceremony. This ends a password that works and every
          session that member has open — there is no "anybody but the
          administrator themselves" case any more, because a self-reset is
          refused outright — and the only copy of the replacement exists until
          this dialog closes. The password is held in component state alone; web storage is
          forbidden for security-relevant state and this is live until it is
          used. */}
      <Modal
        open={activePasswordReset !== null}
        title={
          activePasswordReset?.issued ? "New password" : "Reset this password?"
        }
        /* Gated while the request is in flight, exactly as the local-account
           dialog gates its own (CodeRabbit, PR #275). `Modal` routes its close
           button, Escape and an outside press through here, and closing mid-
           flight let the administrator open a reset for somebody else — whose
           dialog the first response would then replace with the first member's
           password. */
        onClose={() => {
          if (passwordResetPending) return;
          setPasswordReset(null);
        }}
        footer={
          activePasswordReset?.issued ? (
            /* Secondary, like the other credential dialogs: the accent belongs
               on Copy, which is what still has to be done. */
            <Button variant="secondary" onClick={() => setPasswordReset(null)}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                disabled={passwordResetPending}
                onClick={() => setPasswordReset(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={passwordResetPending}
                onClick={() => {
                  if (!activePasswordReset) return;
                  resetPasswordRow.run({
                    operationId: activePasswordReset.operationId,
                    userId: activePasswordReset.member.userId,
                    member: activePasswordReset.member,
                    scope: activePasswordReset.scope,
                  });
                }}
              >
                {passwordResetPending ? "Resetting..." : "Reset password"}
              </Button>
            </>
          )
        }
      >
        {activePasswordReset ? (
          <div className="flex flex-col gap-4">
            <DescriptionList>
              <DescriptionItem label="Member">
                {memberLabel(activePasswordReset.member)}
              </DescriptionItem>
              {activePasswordReset.issued ? (
                <DescriptionItem label="Temporary password">
                  <div className="flex items-center gap-2 rounded-md border border-pb-border bg-pb-card-elevated p-2.5">
                    <code
                      data-testid="reset-temporary-password"
                      className="min-w-0 flex-1 break-all text-sm text-pb-text"
                    >
                      {activePasswordReset.issued.temporaryPassword}
                    </code>
                    <Button
                      variant="primary"
                      className="shrink-0 gap-1.5"
                      onClick={() => void copyTemporaryPasswordReset()}
                    >
                      {passwordResetCopied ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      {passwordResetCopied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                </DescriptionItem>
              ) : null}
            </DescriptionList>
            {activePasswordReset.issued ? (
              <>
                {/* Says what to DO with it, because nothing else will: this
                    workspace has no way to send it, so the administrator is
                    the delivery mechanism. */}
                <p className="text-sm text-pb-text-secondary">
                  Send this to them yourself — we have not emailed it. It cannot
                  be shown again, and they will be asked to choose a new
                  password when they sign in.
                  {
                    {
                      revoked: " They have been signed out everywhere.",
                      incomplete: "",
                    }[activePasswordReset.issued.signOut]
                  }
                </p>
                {/* Each of these is a step that did not finish AFTER the
                    password was already live. Saying so is the point of
                    handing it over anyway. */}
                {activePasswordReset.issued.signOut === "incomplete" ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-pb-red-text" role="alert">
                      {signOutRetryOutcome === "revoked"
                        ? "They have now been signed out everywhere."
                        : signOutRetryOutcome === "failed"
                          ? "Signing them out still did not complete. Their password is already reset; open Sessions on their row to finish it."
                          : "We could not confirm they were signed out everywhere, so they may still have an open session. Their password is already reset."}
                    </p>
                    {signOutRetryOutcome === "revoked" ? null : (
                      <div>
                        <Button
                          type="button"
                          variant="secondary"
                          loading={signOutRetryPending}
                          onClick={() => {
                            const scope = scopeFromClaims(claims);
                            if (!scope) return;
                            retrySignOut.mutate({
                              operationId: activePasswordReset.operationId,
                              userId: activePasswordReset.member.userId,
                              scope,
                            });
                          }}
                        >
                          Sign them out everywhere
                        </Button>
                      </div>
                    )}
                  </div>
                ) : null}
                {activePasswordReset.issued.recorded ? null : (
                  <p className="text-sm text-pb-red-text" role="alert">
                    This reset was not written to the workspace audit log.
                  </p>
                )}
                {passwordResetCopyErrorText ? (
                  <p className="text-sm text-pb-red-text" role="alert">
                    {passwordResetCopyErrorText}
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-pb-text-secondary">
                Their current password stops working immediately. They will be
                signed out everywhere, and we will show you a temporary password
                to pass on.
              </p>
            )}
            {activePasswordReset.issued === null && passwordResetErrorText ? (
              <p className="text-sm text-pb-red-text" role="alert">
                {passwordResetErrorText}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </AdminSection>
  );
}
